import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Plus, Search, Filter, X, Edit, Trash2, Eye, 
  DollarSign, Calendar, User, Building, CheckCircle, 
  Clock, XCircle, AlertCircle, Save, ChevronRight, ChevronDown,
  TrendingUp, BarChart3, Send, Download, Printer, Copy, Link2, Check,
  Receipt, CreditCard, CheckCircle2, Pencil, MessageSquare, History,
  EyeOff, ChevronUp, ChevronLeft, Package, ShoppingCart, Image,
  RefreshCw, AlertTriangle, Tag, Settings, List
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, subWeeks, subMonths, isWithinInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import { proposalService } from '../services/proposalService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { productService } from '../services/productService';
import { companyService } from '../services/companyService';
import { settingsService } from '../services/settingsService';
import { catalogService } from '../services/catalogService';
import { CONTAINER_SPECS as CONTAINER_SPECS_STATIC } from './CustomerOrderManager';
import { customerOrderService } from '../services/customerOrderService';
import { categoryService } from '../services/categoryService';
import { mapProductToFormat, mapProposalToFormat, mapProposalToAPI, mapCompanyToFormat } from '../utils/apiMapper';
import { parseErrorMessage } from '../utils/helpers';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

const STATUS_COLORS = {
  'Borrador': 'bg-gray-100 text-gray-800 border-gray-300',
  'Enviada': 'bg-blue-100 text-blue-800 border-blue-300',
  'Respondida': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Convertida': 'bg-purple-100 text-purple-800 border-purple-300',
  'Cancelada': 'bg-red-100 text-red-800 border-red-300',
  'Aprobada': 'bg-green-100 text-green-800 border-green-300',
};

const STAGE_COLORS = {
  'Baja': 'bg-red-100 text-red-800 border-red-300',
  'Media': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  'Alta': 'bg-green-100 text-green-800 border-green-300',
};

const STATUS_ICONS = {
  'Borrador': Clock,
  'Enviada': Send,
  'Respondida': MessageSquare,
  'Convertida': CheckCircle2,
  'Cancelada': XCircle,
  'Aprobada': CheckCircle,
};

// Datos comerciales que la cotización hereda del contacto seleccionado
const getContactCommercialDefaults = (contact, priceLists = []) => {
  if (!contact) return {};
  const defaults = {};

  const creditDays = contact.creditDaysValue;
  const paymentTerms = contact.paymentTerm
    || [contact.paymentMethodName, creditDays ? `${creditDays} días` : '']
      .filter(Boolean)
      .join(' · ');
  if (paymentTerms) defaults.paymentTerms = paymentTerms;

  const discount = contact.discountPercentage;
  if (discount !== '' && discount !== null && discount !== undefined) {
    defaults.rebate = String(discount);
  }

  const contactPriceList = String(contact.priceList || '').trim().toLowerCase();
  if (contactPriceList) {
    const matched = priceLists.find(pl => (pl.name || '').trim().toLowerCase() === contactPriceList);
    if (matched) defaults.priceListId = String(matched.id);
  }

  return defaults;
};

const ProposalManager = ({
  proposals = [],
  contacts = [],
  theme = 'light',
  onAddProposal = () => {},
  onUpdateProposal = () => {},
  onUpdateProposalProforma = () => {},
  onDeleteProposal = () => {},
  onOpenCustomerOrder = null,
}) => {
  const isDark = theme === 'dark';
  const { showNotification } = useNotification();
  const [generatingOrderId, setGeneratingOrderId] = useState(null);
  const [confirmOrderProposal, setConfirmOrderProposal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all'); // Filtro por etapa (Baja, Media, Alta)
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'current_week', 'last_week', 'current_month', 'last_month', 'q1', 'q2', 'q3', 'q4', 'custom'
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [viewingProposal, setViewingProposal] = useState(null);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showIndicators, setShowIndicators] = useState(false);
  const { user } = useAuth();
  
  // Verificar si el usuario tiene permiso para ver indicadores
  const canViewIndicators = useMemo(() => {
    if (!user || !user.category || !user.category.permissions) {
      return true; // Por defecto mostrar si no hay permisos definidos
    }
    return user.category.permissions['proposals.view_indicators'] !== false;
  }, [user]);


  // Cargar estadísticas, productos y empresas al montar
  useEffect(() => {
    loadStats();
    loadProducts();
    loadCompanies();
    loadPriceLists();
  }, []);

  const loadStats = async () => {
    try {
      const statsData = await proposalService.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const productsData = await productService.getAll();
      const mappedProducts = (productsData || [])
        .map(mapProductToFormat)
        .filter(product => product !== null && product !== undefined);
      setProducts(mappedProducts);
    } catch (error) {
      console.error('Error cargando productos:', error);
      setProducts([]);
    }
  };

  const loadCompanies = async () => {
    try {
      const companiesData = await companyService.getAll();
      const mappedCompanies = (Array.isArray(companiesData) ? companiesData : (companiesData?.results || []))
        .map(mapCompanyToFormat)
        .filter(c => c !== null && c !== undefined);
      setCompanies(mappedCompanies);
    } catch (error) {
      console.error('Error cargando empresas:', error);
      setCompanies([]);
    }
  };

  const loadPriceLists = async () => {
    try {
      const data = await catalogService.priceLists.getAll({ active_only: true });
      setPriceLists(Array.isArray(data) ? data : (data?.results || []));
    } catch (err) {
      console.error('Error cargando listas de precios:', err);
    }
  };

  // Helpers - Definir antes de usar en useMemo
  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId?.toString());
    return contact?.name || 'Contacto desconocido';
  };

  // Función para obtener rango de fechas según el filtro
  const getDateRange = useMemo(() => {
    const now = new Date();
    
    switch (dateFilter) {
      case 'current_week':
        return {
          start: startOfWeek(now, { weekStartsOn: 1 }),
          end: endOfWeek(now, { weekStartsOn: 1 })
        };
      case 'last_week':
        const lastWeek = subWeeks(now, 1);
        return {
          start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          end: endOfWeek(lastWeek, { weekStartsOn: 1 })
        };
      case 'current_month':
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth)
        };
      case 'q1':
        return {
          start: startOfQuarter(new Date(now.getFullYear(), 0, 1)),
          end: endOfQuarter(new Date(now.getFullYear(), 0, 1))
        };
      case 'q2':
        return {
          start: startOfQuarter(new Date(now.getFullYear(), 3, 1)),
          end: endOfQuarter(new Date(now.getFullYear(), 3, 1))
        };
      case 'q3':
        return {
          start: startOfQuarter(new Date(now.getFullYear(), 6, 1)),
          end: endOfQuarter(new Date(now.getFullYear(), 6, 1))
        };
      case 'q4':
        return {
          start: startOfQuarter(new Date(now.getFullYear(), 9, 1)),
          end: endOfQuarter(new Date(now.getFullYear(), 9, 1))
        };
      case 'custom':
        if (customDateStart && customDateEnd) {
          return {
            start: new Date(customDateStart),
            end: new Date(customDateEnd)
          };
        }
        return null;
      default:
        return null;
    }
  }, [dateFilter, customDateStart, customDateEnd]);

  // Filtrar propuestas
  const filteredProposals = useMemo(() => {
    return proposals.filter(proposal => {
      const matchesSearch = !searchTerm || 
        proposal.proposalId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getContactName(proposal.contactId)?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || proposal.status === statusFilter;
      
      // Filtrar por etapa
      const matchesStage = stageFilter === 'all' || (proposal.stage || 'Media') === stageFilter;
      
      // Filtrar por fecha
      let matchesDate = true;
      if (getDateRange) {
        const proposalDate = new Date(proposal.createdAt);
        matchesDate = isWithinInterval(proposalDate, {
          start: getDateRange.start,
          end: getDateRange.end
        });
      }
      
      return matchesSearch && matchesStatus && matchesStage && matchesDate;
    });
  }, [proposals, searchTerm, statusFilter, stageFilter, contacts, getDateRange]);

  // Calcular el costo total de una propuesta (desde items estructurados)
  const calculateProposalCost = useCallback((proposal) => {
    const items = proposal.items || [];
    if (items.length === 0) return 0;

    let totalCost = 0;
    items.forEach(item => {
      if (item.product_id) {
        const product = products.find(p => p.id === item.product_id?.toString());
        if (product && product.cost) {
          const quantity = parseInt(item.quantity) || 0;
          totalCost += product.cost * quantity;
        }
      }
    });
    return totalCost;
  }, [products]);

  // Calcular estadísticas basadas en propuestas filtradas
  const calculatedStats = useMemo(() => {
    // Total Propuestas: todas las propuestas filtradas
    const totalProposals = filteredProposals.length;
    
    // Total Cotizado: suma de totalAmount de propuestas con estado "Enviada"
    // Si hay filtro de estado, solo contar si el filtro es "Enviada" o "all"
    const quotedProposals = statusFilter === 'all' 
      ? filteredProposals.filter(p => p.status === 'Enviada')
      : (statusFilter === 'Enviada' ? filteredProposals : []);
    const totalQuoted = quotedProposals.reduce((sum, p) => {
      return sum + (parseFloat(p.totalAmount) || 0);
    }, 0);
    
    // Abonos ya no existen como sección separada – todo es items
    let totalAbonosQuoted = 0;
    
    // Total Aprobado: suma de totalAmount de propuestas con estado "Aprobada"
    // Si hay filtro de estado, solo contar si el filtro es "Aprobada" o "all"
    const approvedProposals = statusFilter === 'all'
      ? filteredProposals.filter(p => p.status === 'Aprobada')
      : (statusFilter === 'Aprobada' ? filteredProposals : []);
    const totalApproved = approvedProposals.reduce((sum, p) => {
      return sum + (parseFloat(p.totalAmount) || 0);
    }, 0);
    
    // Conversión: (Total Aprobado / Total Cotizado) * 100
    const conversionRate = totalQuoted > 0 ? (totalApproved / totalQuoted) * 100 : 0;
    
    // Estadísticas por etapa
    const stageStats = {
      Baja: { count: 0, quoted: 0, cost: 0 },
      Media: { count: 0, quoted: 0, cost: 0 },
      Alta: { count: 0, quoted: 0, cost: 0 },
    };
    
    filteredProposals.forEach(proposal => {
      const stage = proposal.stage || 'Media';
      if (stageStats[stage]) {
        stageStats[stage].count++;
        // Valor cotizado (totalAmount)
        stageStats[stage].quoted += parseFloat(proposal.totalAmount) || 0;
        // Costo total
        stageStats[stage].cost += calculateProposalCost(proposal);
      }
    });
    
    return {
      total_proposals: totalProposals,
      total_quoted: totalQuoted,
      total_abonos_quoted: totalAbonosQuoted,
      total_approved: totalApproved,
      conversion_rate: Math.round(conversionRate * 100) / 100, // Redondear a 2 decimales
      stage_stats: stageStats
    };
  }, [filteredProposals, statusFilter, products, calculateProposalCost]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleCreateProposal = () => {
    setShowCreateModal(true);
  };

  const handleEditProposal = (proposal) => {
    setEditingProposal(proposal);
  };

  const handleDuplicateProposal = async (proposal) => {
    try {
      // Crear una copia de la propuesta sin el ID y proposalIdStr
      // El backend generará automáticamente un nuevo ID de propuesta
      const duplicatedProposal = {
        contactId: proposal.contactId,
        companyId: proposal.companyId || null,
        status: 'Borrador',
        greeting: proposal.greeting || '',
        items: (proposal.items || []).map(item => ({
          product_id: item.product_id,
          quantity: item.quantity || 1,
          price_fob_unit: item.price_fob_unit || 0,
          moq: item.moq || null,
        })),
      };

      // Crear la nueva propuesta
      const created = await onAddProposal(duplicatedProposal);
      setEditingProposal(created);
      await loadStats();
      alert('Cotización duplicada exitosamente');
    } catch (error) {
      console.error('Error duplicando cotización:', error);
      alert(`No se pudo duplicar la cotización.\n${parseErrorMessage(error)}`);
    }
  };

  const handleDeleteProposal = async (proposalId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta cotización?')) {
      try {
        await onDeleteProposal(proposalId);
        await loadStats(); // Actualizar estadísticas
      } catch (error) {
        alert(`No se pudo eliminar la cotización.\n${parseErrorMessage(error)}`);
      }
    }
  };

  const handleGenerateCustomerOrder = (proposal) => {
    if (!proposal?.id) return;
    if (proposal.status !== 'Aprobada') {
      showNotification(
        "Solo se pueden generar Pedidos a partir de cotizaciones en estado 'Aprobada'.",
        'warning'
      );
      return;
    }
    if (proposal.customerOrderId) {
      showNotification(
        `La cotización ya tiene el Pedido ${proposal.customerOrderCode || 'PD-?'} generado.`,
        'info'
      );
      return;
    }
    setConfirmOrderProposal(proposal);
  };

  const handleConfirmGenerateOrder = async () => {
    const proposal = confirmOrderProposal;
    if (!proposal?.id) return;
    try {
      setGeneratingOrderId(proposal.id);
      setConfirmOrderProposal(null);
      const created = await customerOrderService.generateFromProposal(proposal.id);
      const code = created?.order_code || 'PD-?';
      showNotification(
        `Pedido ${code} generado con éxito desde la cotización ${proposal.proposalId}.`,
        'success'
      );
      try { await loadStats(); } catch (_) {}
      if (onOpenCustomerOrder && created?.id) {
        onOpenCustomerOrder(created.id);
      }
    } catch (error) {
      console.error('Error generando Pedido desde cotización:', error);
      showNotification(
        `No se pudo generar el Pedido.\n${parseErrorMessage(error)}`,
        'error'
      );
    } finally {
      setGeneratingOrderId(null);
    }
  };


  const handleGeneratePDF = async (proposal) => {
    try {
      const element = document.getElementById(`proposal-view-${proposal.id}`);
      if (!element) {
        alert('No se pudo generar el PDF. Intentá abrir la vista previa nuevamente.');
        return;
      }

      // Esperar un momento para que el DOM se renderice completamente
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Obtener todas las páginas
      const allPages = element.querySelectorAll('.proposal-page');
      
      if (allPages.length === 0) {
        alert('La cotización no tiene productos para mostrar en el PDF. Agregá al menos un producto.');
        return;
      }
      
      // Filtrar páginas con contenido real
      const pages = Array.from(allPages).filter(page => {
        const textContent = page.textContent || '';
        const hasVisibleContent = textContent.trim().length > 0;
        const hasTextInElements = Array.from(page.querySelectorAll('h2, h3, p, td, th, div, table')).some(
          el => el.textContent && el.textContent.trim().length > 0
        );
        return hasVisibleContent && hasTextInElements;
      });

      if (pages.length === 0) {
        alert('La cotización no tiene contenido para exportar. Agregá productos e intentá de nuevo.');
        return;
      }

      // Crear PDF con dimensiones A4 exactas
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm

      // Configuración optimizada para html2canvas
      // A4 en píxeles a 96 DPI: 210mm = 794px, 297mm = 1123px
      // Usamos escala 2.5 para buena calidad sin ser excesiva
      const scale = 2.5;
      const dpi = 96;
      const mmToPx = dpi / 25.4; // 3.779527559
      const pageWidthPx = 210 * mmToPx; // ~794px
      const pageHeightPx = 297 * mmToPx; // ~1123px

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        
        try {
          // Obtener dimensiones reales de la página
          const pageWidth = page.offsetWidth || pageWidthPx;
          const pageHeight = page.offsetHeight || pageHeightPx;
          
          const canvas = await html2canvas(page, {
            scale: scale,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            allowTaint: false,
            removeContainer: false,
            imageTimeout: 15000,
            width: pageWidth,
            height: pageHeight,
            windowWidth: pageWidth,
            windowHeight: pageHeight,
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0
          });

          const imgData = canvas.toDataURL('image/png', 1.0);
          const imgWidth = canvas.width;
          const imgHeight = canvas.height;

          // Calcular dimensiones para ajustar exactamente a A4 (210mm x 297mm)
          // El canvas tiene escala, así que necesitamos ajustar
          const ratio = pdfWidth / (imgWidth / scale);
          const scaledWidth = pdfWidth; // Siempre usar ancho completo de A4
          const scaledHeight = (imgHeight / scale) * ratio;

          // Si no es la primera página, agregar nueva página
          if (i > 0) {
            pdf.addPage();
          }

          // Agregar imagen ajustada a A4 (sin centrar, usar todo el espacio)
          pdf.addImage(imgData, 'PNG', 0, 0, scaledWidth, scaledHeight, undefined, 'FAST');
        } catch (pageError) {
          console.error(`Error procesando página ${i + 1}:`, pageError);
          // Continuar con la siguiente página si hay error
          if (i === 0) {
            throw pageError; // Si falla la primera página, lanzar error
          }
        }
      }

      pdf.save(`Cotizacion_${proposal.proposalId}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('No se pudo generar el PDF. Por favor intentá de nuevo.');
    }
  };

  return (
    <div className="space-y-4">
      {showCreateModal ? (
        <>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Volver a Cotizaciones
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">Nueva Cotización</span>
          </div>
          <CreateProposalModal
            contacts={contacts}
            companies={companies}
            priceLists={priceLists}
            onClose={() => setShowCreateModal(false)}
            onSave={async (proposalData) => {
              try {
                const created = await onAddProposal(proposalData);
                setShowCreateModal(false);
                setEditingProposal(created);
                await loadStats();
              } catch (error) {
                alert(`No se pudo crear la cotización.\n${parseErrorMessage(error)}`);
              }
            }}
          />
        </>
      ) : editingProposal ? (
        <>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setEditingProposal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Volver a Cotizaciones
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">Editar Cotización</span>
          </div>
          <ProposalEditor
            proposal={editingProposal}
            contacts={contacts}
            products={products}
            companies={companies}
            priceLists={priceLists}
            theme={theme}
            onClose={() => setEditingProposal(null)}
            onView={(updatedProposal) => {
              setEditingProposal(null);
              setViewingProposal(updatedProposal || editingProposal);
            }}
            onGeneratePDF={handleGeneratePDF}
            onGenerateCustomerOrder={async (created) => {
              const code = created?.order_code || 'PD-?';
              showNotification(
                `Pedido ${code} generado con éxito desde la cotización ${editingProposal.proposalId}.`,
                'success'
              );
              try { await loadStats(); } catch (_) {}
              if (onOpenCustomerOrder && created?.id) {
                onOpenCustomerOrder(created.id);
              }
            }}
            onSave={async (updates) => {
              try {
                const updated = await onUpdateProposal(editingProposal.id, updates);
                await loadStats();
                if (updates.status === 'Aprobada' && editingProposal.status !== 'Aprobada') {
                }
                return updated;
              } catch (error) {
                console.error('Error guardando cotización:', error);
                const errorMessage = error?.response?.data?.detail || 
                                    error?.response?.data?.message || 
                                    error?.message || 
                                    'Error desconocido al guardar la cotización';
                alert(`No se pudo guardar la cotización.\n${parseErrorMessage(error, 'Revisá los datos del cliente y los productos e intentá de nuevo.')}`);
                throw error;
              }
            }}
          />
        </>
      ) : viewingProposal ? (
        <>
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => setViewingProposal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              Volver a Cotizaciones
            </button>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">Ver Cotización</span>
          </div>
          <ProposalView
            proposal={viewingProposal}
            contacts={contacts}
            onClose={() => setViewingProposal(null)}
            onGeneratePDF={handleGeneratePDF}
          />
        </>
      ) : (
        <>
      {/* Header con estadísticas */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Cotizaciones Comerciales
            </h1>
            <p className="text-gray-600 mt-1">Gestiona y analiza tus cotizaciones comerciales</p>
          </div>
          <button
            onClick={handleCreateProposal}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm transition-all duration-300 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Crear Cotización
          </button>
        </div>

        {/* Botón para mostrar/ocultar indicadores */}
        {canViewIndicators && (
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setShowIndicators(!showIndicators)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {showIndicators ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  Ocultar Indicadores
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  Mostrar Indicadores
                </>
              )}
            </button>
          </div>
        )}

        {/* Estadísticas rápidas */}
        {canViewIndicators && showIndicators && (
          <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-gray-600">Total Cotizaciones</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{calculatedStats.total_proposals || 0}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-600">Total Cotizado</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{formatCurrency(calculatedStats.total_quoted || 0)}</p>
          </div>
          <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-teal-600" />
              <span className="text-sm text-gray-600">Total Aprobado</span>
            </div>
            <p className="text-2xl font-bold text-teal-900">{formatCurrency(calculatedStats.total_approved || 0)}</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              <span className="text-sm text-gray-600">Conversión</span>
            </div>
            <p className="text-2xl font-bold text-orange-900">{calculatedStats.conversion_rate || 0}%</p>
          </div>
        </div>

        {/* Indicadores por Etapa — ocultos */}
        {false && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {['Baja', 'Media', 'Alta'].map(stage => {
            const stats = calculatedStats.stage_stats?.[stage] || { count: 0, quoted: 0, cost: 0 };
            const bgColor = stage === 'Baja' ? 'bg-red-50 border-red-200' :
                           stage === 'Media' ? 'bg-yellow-50 border-yellow-200' :
                           'bg-green-50 border-green-200';
            const textColor = stage === 'Baja' ? 'text-red-600' :
                             stage === 'Media' ? 'text-yellow-600' :
                             'text-green-600';
            const titleColor = stage === 'Baja' ? 'text-red-900' :
                              stage === 'Media' ? 'text-yellow-900' :
                              'text-green-900';
            
            return (
              <div key={stage} className={`${bgColor} rounded-xl p-4 border`}>
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className={`w-5 h-5 ${textColor}`} />
                  <span className={`text-sm font-semibold ${titleColor}`}>{stage}</span>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-xs text-gray-600">Cantidad:</span>
                    <p className={`text-xl font-bold ${titleColor}`}>{stats.count}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600">Valor Cotizado:</span>
                    <p className={`text-lg font-semibold ${titleColor}`}>{formatCurrency(stats.quoted)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-600">Costo Total:</span>
                    <p className={`text-lg font-semibold ${titleColor}`}>{formatCurrency(stats.cost)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>}
          </>
        )}
      </div>

      {/* Filtros y búsqueda */}
      <div
        className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex-1 sm:max-w-xs relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por ID o contacto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              >
                <option value="all">Todos los estados</option>
                <option value="Borrador">Borrador</option>
                <option value="Enviada">Enviada</option>
                <option value="Respondida">Respondida</option>
                <option value="Convertida">Convertida a Pedido</option>
                <option value="Cancelada">Cancelada</option>
                <option value="Aprobada">Aprobada</option>
              </select>
            </div>
            {/* Filtro de etapas — oculto */}
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gray-400" />
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              >
                <option value="all">Todas las fechas</option>
                <option value="current_week">Semana actual</option>
                <option value="last_week">Semana pasada</option>
                <option value="current_month">Mes actual</option>
                <option value="last_month">Mes pasado</option>
                <option value="q1">Q1</option>
                <option value="q2">Q2</option>
                <option value="q3">Q3</option>
                <option value="q4">Q4</option>
                <option value="custom">Rango de fechas</option>
              </select>
            </div>
          </div>
          
          {/* Campos de fecha personalizada */}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateStart}
                onChange={(e) => setCustomDateStart(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                placeholder="Desde"
              />
              <span className="text-gray-500">hasta</span>
              <input
                type="date"
                value={customDateEnd}
                onChange={(e) => setCustomDateEnd(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                placeholder="Hasta"
              />
            </div>
          )}
        </div>
      </div>

      {/* Lista de cotizaciones */}
      <div className="grid gap-4">
        {filteredProposals.length === 0 ? (
          <div
            className="bg-white border border-gray-200 rounded-xl p-12 text-center"
          >
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No hay cotizaciones para mostrar</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm || statusFilter !== 'all' 
                ? 'Intenta ajustar los filtros' 
                : 'Crea tu primera cotización comercial'}
            </p>
          </div>
        ) : (
          filteredProposals.map((proposal, index) => {
            const StatusIcon = STATUS_ICONS[proposal.status] || Clock;
            return (
              <div
                key={proposal.id}
                className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                onClick={() => handleEditProposal(proposal)}
              >
                <div className="flex flex-col gap-4">
                  {/* Header con ID, estado y controles superiores */}
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-bold text-gray-800">{proposal.proposalId}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[proposal.status] || STATUS_COLORS['Borrador']}`}>
                        <StatusIcon className="w-3 h-3 inline mr-1" />
                        {proposal.status}
                      </span>
                      {/* badge etapa — oculto */}
                      {/* checkbox proforma — oculto */}
                      {proposal.customerOrderId && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenCustomerOrder) {
                              onOpenCustomerOrder(proposal.customerOrderId);
                            }
                          }}
                          className="px-3 py-1 rounded-full text-xs font-semibold border bg-purple-100 text-purple-800 border-purple-300 hover:bg-purple-200 transition-colors flex items-center gap-1"
                          title="Ir al pedido generado"
                        >
                          <ShoppingCart className="w-3 h-3" />
                          Ver pedido {proposal.customerOrderCode || 'PD-'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{getContactName(proposal.contactId)}</span>
                      </div>
                      {proposal.createdByName && (
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span>Creado por: <span className="font-medium text-gray-600">{proposal.createdByName}</span></span>
                        </div>
                      )}
                      {/* Facturar / Costo — ocultos */}
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{format(new Date(proposal.createdAt), 'dd/MM/yyyy')}</span>
                      </div>
                    </div>
                    
                    {/* Indicadores de archivos adjuntos */}
                    {proposal.invoiceFile && (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                        {proposal.invoiceFile && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded flex items-center gap-1">
                            <Receipt className="w-3 h-3" />
                            Factura
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {proposal.status === 'Aprobada' && !proposal.customerOrderId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateCustomerOrder(proposal);
                        }}
                        disabled={generatingOrderId === proposal.id}
                        className="px-3 py-2 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 rounded-lg transition-colors flex items-center gap-2"
                        title="Generar Pedido a partir de esta cotización"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {generatingOrderId === proposal.id ? 'Generando…' : 'Generar Pedido'}
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicateProposal(proposal);
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Duplicar cotización"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProposal(proposal);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Editar cotización"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProposal(proposal.id);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Eliminar cotización"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
        </>
      )}

      {/* Modal de confirmación global: Generar Pedido desde el listado */}
      {confirmOrderProposal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                <ShoppingCart className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Convertir a Pro Forma</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                ¿Usted está seguro de cambiar esta cotización en Pro forma?
                <br />
                <span className="font-semibold text-gray-800">Se generará un pedido automáticamente.</span>
              </p>
              <div className="flex gap-3 w-full pt-2">
                <button
                  onClick={() => setConfirmOrderProposal(null)}
                  disabled={!!generatingOrderId}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmGenerateOrder}
                  disabled={!!generatingOrderId}
                  className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {generatingOrderId ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generando...
                    </>
                  ) : 'Aceptar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Modal de creación inicial de cotización
const CreateProposalModal = ({ contacts, companies = [], priceLists = [], onClose, onSave }) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [searchContact, setSearchContact] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [greeting, setGreeting] = useState('');
  const [salutation, setSalutation] = useState('Sr.');
  const [loading, setLoading] = useState(false);
  const [nextProposalId, setNextProposalId] = useState('O02000');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const contactInputContainerRef = useRef(null);
  const contactDropdownRef = useRef(null);

  // Cargar último saludo y calcular siguiente ID
  useEffect(() => {
    const loadLastGreeting = async () => {
      try {
        const lastGreeting = await proposalService.getLastGreeting();
        if (lastGreeting) {
          setGreeting(lastGreeting);
        }
      } catch (error) {
        console.error('Error cargando último saludo:', error);
      }
    };
    loadLastGreeting();
  }, []);

  // Contactos filtrados por empresa (si hay empresa seleccionada)
  const contactsByCompany = useMemo(() => {
    if (!selectedCompanyId) return contacts;
    return contacts.filter(c => {
      const companyIds = Array.isArray(c.companies) ? c.companies.map((company) => String(company.id)) : [];
      return companyIds.includes(String(selectedCompanyId));
    });
  }, [contacts, selectedCompanyId]);

  // Filtrar contactos según búsqueda - mostrar todos si no hay texto
  const filteredContacts = useMemo(() => {
    if (!searchContact.trim()) {
      return contactsByCompany.slice(0, 50);
    }
    const searchLower = searchContact.toLowerCase();
    return contactsByCompany.filter(contact =>
      contact.name?.toLowerCase().includes(searchLower) ||
      contact.email?.toLowerCase().includes(searchLower)
    ).slice(0, 50);
  }, [contactsByCompany, searchContact]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contactInputContainerRef.current && !contactInputContainerRef.current.contains(event.target) &&
        contactDropdownRef.current && !contactDropdownRef.current.contains(event.target)
      ) {
        setShowContactDropdown(false);
        setFocusedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleContactSelect = (contact) => {
    setSelectedContact(contact);
    setSearchContact(contact.name);
    setShowContactDropdown(false);
    setFocusedIndex(-1);
  };

  // Scroll al elemento enfocado cuando se navega con teclado
  useEffect(() => {
    if (focusedIndex >= 0 && contactDropdownRef.current) {
      const focusedElement = contactDropdownRef.current.children[focusedIndex];
      if (focusedElement) {
        focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [focusedIndex]);

  // Manejar navegación con teclado
  const handleKeyDown = (e) => {
    if (!showContactDropdown || filteredContacts.length === 0) {
      // Si el dropdown no está abierto, abrirlo con las flechas
      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !showContactDropdown) {
        e.preventDefault();
        setShowContactDropdown(true);
        setFocusedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredContacts.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredContacts.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredContacts.length) {
          handleContactSelect(filteredContacts[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowContactDropdown(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedContact) {
      alert('Por favor selecciona un contacto');
      return;
    }

    setLoading(true);
    try {
      const proposalData = {
        contactId: selectedContact.id,
        companyId: selectedCompanyId || null,
        status: 'Borrador',
        greeting: greeting.trim() || null,
        salutation,
        items: [],
        ...getContactCommercialDefaults(selectedContact, priceLists),
      };

      await onSave(proposalData);
    } catch (error) {
      console.error('Error creando cotización:', error);
      alert(`No se pudo crear la cotización.\n${parseErrorMessage(error, 'Por favor intentá de nuevo.')}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm max-w-xl mx-auto">
      <div className="p-8">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Nueva Cotización</h2>
          <p className="text-sm text-gray-500 mt-1">Seleccioná el contacto para comenzar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Buscador de Contacto */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Contacto <span className="text-red-500">*</span>
            </label>
            <div className="relative" ref={contactInputContainerRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o empresa..."
                value={searchContact}
                onChange={(e) => {
                  setSearchContact(e.target.value);
                  setShowContactDropdown(true);
                  setFocusedIndex(-1);
                  if (!e.target.value) setSelectedContact(null);
                }}
                onFocus={() => { setShowContactDropdown(true); setFocusedIndex(-1); }}
                onKeyDown={handleKeyDown}
                className="w-full h-11 pl-9 pr-4 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm"
                required
              />

              {showContactDropdown && !selectedContact && filteredContacts.length > 0 && (
                <div ref={contactDropdownRef} className="absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {filteredContacts.map((contact, index) => {
                    const company = contact.companies?.[0]?.name || '';
                    return (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => handleContactSelect(contact)}
                        onMouseEnter={() => setFocusedIndex(index)}
                        onMouseDown={(e) => e.preventDefault()}
                        className={`w-full px-4 py-3 text-left flex items-center gap-3 border-b border-gray-50 last:border-b-0 transition-colors ${index === focusedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-blue-700">{(contact.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 text-sm truncate">{contact.name}</div>
                          {company && <div className="text-xs text-gray-500 truncate">{company}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {showContactDropdown && !selectedContact && filteredContacts.length === 0 && searchContact.trim() && (
                <div ref={contactDropdownRef} className="absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">No se encontraron contactos</div>
                </div>
              )}
            </div>

            {selectedContact && (
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-200 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-blue-800">{(selectedContact.name || '?')[0].toUpperCase()}</span>
                </div>
                <div>
                  <div className="font-semibold text-blue-900">{selectedContact.name}</div>
                  {selectedContact.companies?.[0]?.name && (
                    <div className="text-sm text-blue-700">{selectedContact.companies[0].name}</div>
                  )}
                  {selectedContact.email && (
                    <div className="text-xs text-blue-600">{selectedContact.email}</div>
                  )}
                </div>
                <button type="button" onClick={() => { setSelectedContact(null); setSearchContact(''); }}
                  className="ml-auto text-blue-400 hover:text-blue-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 px-4 py-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={loading || !selectedContact}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Creando...</>
              ) : (
                <>Crear cotización <ChevronRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Editor completo de propuesta
const ProposalEditor = ({ proposal, contacts, products, companies = [], priceLists = [], theme = 'light', onClose, onSave, onView, onGeneratePDF, onGenerateCustomerOrder = null }) => {
  const { user } = useAuth();
  const [proposalData, setProposalData] = useState(proposal);
  const [activeSection, setActiveSection] = useState('client');
  const [saving, setSaving] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [convertingOrder, setConvertingOrder] = useState(false);
  const [containerSpecs, setContainerSpecs] = useState(CONTAINER_SPECS_STATIC);
  const selectRef = useRef(null);

  useEffect(() => {
    customerOrderService.getContainerSpecs()
      .then(data => { if (data && typeof data === 'object') setContainerSpecs(data); })
      .catch(() => {});
  }, []);

  // Resumen logístico calculado desde los ítems de la cotización
  const logisticsSummary = useMemo(() => {
    const items = proposalData.items || [];
    let totalCartons = 0;
    let totalCbm = 0;
    let totalPcs = 0;
    items.forEach(it => {
      const qty = parseInt(it.quantity) || 0;
      const pcs = parseInt(it.pcs_per_ctn) || 0;
      const cbm = parseFloat(it.cbm_per_ctn) || 0;
      totalPcs += qty;
      const ctns = pcs > 0 ? Math.ceil(qty / pcs) : 0;
      totalCartons += ctns;
      totalCbm += ctns * cbm;
    });

    // Suggest the smallest container that fits by CBM
    let suggestedContainer = null;
    const orderedTypes = ['20ft', '40ft', '40hc'];
    for (const type of orderedTypes) {
      const spec = containerSpecs[type];
      if (spec && totalCbm <= spec.cbm) { suggestedContainer = { type, spec, count: 1 }; break; }
    }
    if (!suggestedContainer && totalCbm > 0) {
      const hcSpec = containerSpecs['40hc'];
      if (hcSpec) {
        suggestedContainer = { type: '40hc', spec: hcSpec, count: Math.ceil(totalCbm / hcSpec.cbm) };
      }
    }

    return { totalPcs, totalCartons, totalCbm, suggestedContainer, hasData: totalPcs > 0 };
  }, [proposalData.items, containerSpecs]);
  // Sincronizar el estado cuando cambia la propuesta original
  useEffect(() => {
    setProposalData(proposal);
  }, [proposal.id]);

  // Inicializar items si no existe
  useEffect(() => {
    if (!proposalData.items) {
      setProposalData(prev => ({ ...prev, items: [] }));
    }
  }, []);

  const getContact = () => {
    return contacts.find(c => c.id === proposalData.contactId?.toString());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Calcular total general desde items
  const calculateProposalTotal = () => {
    return (proposalData.items || []).reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (!proposalData.contactId) {
        alert('El contacto es obligatorio. Seleccioná un contacto antes de guardar.');
        setSaving(false);
        return;
      }
      
      // Validar items sin producto seleccionado
      const invalidItems = (proposalData.items || []).filter(item => !item.product_id);
      if (invalidItems.length > 0) {
        const shouldContinue = window.confirm(
          `Advertencia: Hay ${invalidItems.length} item(s) sin producto seleccionado.\n\n` +
          `Estos items no se guardarán en la cotización.\n\n` +
          `¿Deseas continuar?`
        );
        if (!shouldContinue) {
          setSaving(false);
          return;
        }
      }
      
      const total = calculateProposalTotal();
      
      const cleanProposalData = {
        id: proposalData.id,
        proposalId: proposalData.proposalId || '',
        contactId: proposalData.contactId,
        companyId: proposalData.companyId || null,
        status: proposalData.status || 'Borrador',
        proforma: proposalData.proforma !== undefined ? proposalData.proforma : false,
        stage: proposalData.stage || 'Media',
        greeting: proposalData.greeting || '',
        salutation: proposalData.salutation || 'Sr.',
        commercial_note: proposalData.commercial_note || '',
        paymentTerms: proposalData.paymentTerms || '',
        rebate: proposalData.rebate ?? '',
        items: (proposalData.items || []).filter(item => item.product_id),
        totalAmount: total
      };
      
      const updatedData = mapProposalToAPI(cleanProposalData);
      await onSave(updatedData);
    } catch (error) {
      console.error('Error guardando cotización:', error);
      const errorMessage = error?.response?.data?.detail || 
                          error?.response?.data?.message || 
                          error?.message || 
                          'Error desconocido al guardar la cotización';
      alert(`No se pudo guardar la cotización.\n${parseErrorMessage(error, 'Revisá los datos del cliente y los productos e intentá de nuevo.')}`);
    } finally {
      setSaving(false);
    }
  };

  // ─── Handlers para items estructurados ───

  const handleAddItem = () => {
    setProposalData(prev => ({
      ...prev,
      items: [...(prev.items || []), {
        product_id: null,
        product_name: '',
        product_code: '',
        brand_name: '',
        category_name: '',
        factory_name: '',
        pcs_per_ctn: null,
        cbm_per_ctn: null,
        moq: null,
        quantity: 1,
        price_fob_unit: 0,
        total: 0,
      }]
    }));
  };

  const handleUpdateItem = (itemIndex, updates) => {
    setProposalData(prev => {
      const items = [...(prev.items || [])];
      if (!items[itemIndex]) return prev;
      
      const item = { ...items[itemIndex], ...updates };
      // Recalcular total de línea
      item.total = (parseInt(item.quantity) || 0) * (parseFloat(item.price_fob_unit) || 0);
      items[itemIndex] = item;
      
      return { ...prev, items };
    });
  };

  const handleDeleteItem = (itemIndex) => {
    setProposalData(prev => {
      const items = [...(prev.items || [])];
      items.splice(itemIndex, 1);
      return { ...prev, items };
    });
  };

  const handleClearItems = () => {
    setProposalData(prev => ({ ...prev, items: [] }));
  };

  const handleProductSelect = async (product, itemIndex) => {
    // Intentar obtener precio de la lista asignada a la propuesta
    let price = parseFloat(product.price_fob) || 0;
    const priceListId = proposalData.priceListId;
    if (priceListId && product.id) {
      try {
        const res = await catalogService.priceLists.getProductPrice(product.id, priceListId);
        if (res?.price_fob_unit !== null && res?.price_fob_unit !== undefined) {
          price = parseFloat(res.price_fob_unit);
        }
      } catch (e) {
        // Fallback al precio base
      }
    }
    const allImages = [product.main_image, ...(product.gallery_images || [])].filter(Boolean).slice(0, 4);
    handleUpdateItem(itemIndex, {
      product_id: product.id ? parseInt(product.id) : null,
      product_name: product.name || '',
      product_code: product.product_code || '',
      brand_name: product.brand_name || '',
      category_name: product.category || '',
      factory_name: product.factory_name || '',
      pcs_per_ctn: product.pcs_per_ctn || null,
      cbm_per_ctn: product.cbm_per_ctn || null,
      moq: product.moq || null,
      product_image: product.main_image || '',
      product_images: allImages,
      price_fob_unit: price,
      quantity: product.moq || 1,
      product_variant: product.product_variant || '',
      packaging_type_name: product.packaging_type_name || '',
      inners: product.inner_ctn || null,
      manufacturer_code: product.manufacturer_code || '',
      material_composition: product.material_composition || '',
      recommended_age: product.recommended_age || '',
      battery_info: product.battery_info || '',
      product_size: product.size || '',
      description: product.description || '',
      product_height: product.product_height ?? null,
      product_length: product.product_length ?? null,
      product_width: product.product_width ?? null,
      packaging_height: product.packaging_height ?? null,
      packaging_length: product.packaging_length ?? null,
      packaging_width: product.packaging_width ?? null,
      pieces: product.pieces ?? null,
      moq_oem: product.moq_oem ?? null,
      assortment: product.assortment || '',
      discount_fob: product.discount_fob ?? null,
      master_pack_height: product.master_pack_height ?? null,
      master_pack_length: product.master_pack_length ?? null,
      master_pack_width: product.master_pack_width ?? null,
      master_pack_gw: product.master_pack_gross_weight ?? null,
      master_pack_nw: product.master_pack_net_weight ?? null,
      port_name: product.factory_port_name || '',
      link_web: product.video_url || '',
    });
  };

  const handleBulkAddProducts = async (selectedProducts, priceList) => {
    // Si hay lista de precios, precargamos los precios de esa lista de una sola llamada
    let priceListItemsMap = {};
    if (priceList?.id) {
      try {
        const plItems = await catalogService.priceLists.getItems(priceList.id, true);
        const itemsArr = Array.isArray(plItems) ? plItems : plItems?.results || [];
        itemsArr.forEach(pli => {
          priceListItemsMap[pli.product] = parseFloat(pli.price_fob_unit);
        });
      } catch (e) {
        console.warn('No se pudieron cargar precios de la lista, usando precio base.', e);
      }
    }

    const newItems = selectedProducts.map(product => {
      const productId = product.id ? parseInt(product.id) : null;
      // Prioridad: precio de la lista → precio base del producto
      const price = (priceList && priceListItemsMap[productId] !== undefined)
        ? priceListItemsMap[productId]
        : (parseFloat(product.price_fob) || 0);
      const qty = parseInt(product.quantity) || product.moq || 1;
      const allImages = [product.main_image, ...(product.gallery_images || [])].filter(Boolean).slice(0, 4);
      return {
        product_id: productId,
        product_name: product.name || '',
        product_code: product.product_code || '',
        brand_name: product.brand_name || '',
        category_name: product.category || '',
        factory_name: product.factory_name || '',
        pcs_per_ctn: product.pcs_per_ctn || null,
        cbm_per_ctn: product.cbm_per_ctn || null,
        moq: product.moq || null,
        product_image: product.main_image || '',
        product_images: allImages,
        quantity: qty,
        price_fob_unit: price,
        total: qty * price,
        product_variant: product.product_variant || '',
        packaging_type_name: product.packaging_type_name || '',
        inners: product.inner_ctn || null,
        manufacturer_code: product.manufacturer_code || '',
        material_composition: product.material_composition || '',
        recommended_age: product.recommended_age || '',
        battery_info: product.battery_info || '',
        product_size: product.size || '',
        description: product.description || '',
        product_height: product.product_height ?? null,
        product_length: product.product_length ?? null,
        product_width: product.product_width ?? null,
        packaging_height: product.packaging_height ?? null,
        packaging_length: product.packaging_length ?? null,
        packaging_width: product.packaging_width ?? null,
        pieces: product.pieces ?? null,
        moq_oem: product.moq_oem ?? null,
        assortment: product.assortment || '',
        discount_fob: product.discount_fob ?? null,
        master_pack_height: product.master_pack_height ?? null,
        master_pack_length: product.master_pack_length ?? null,
        master_pack_width: product.master_pack_width ?? null,
        master_pack_gw: product.master_pack_gross_weight ?? null,
        master_pack_nw: product.master_pack_net_weight ?? null,
        port_name: product.factory_port_name || '',
        link_web: product.video_url || '',
      };
    });

    setProposalData(prev => ({
      ...prev,
      items: [...(prev.items || []), ...newItems],
      ...(priceList ? { priceListId: String(priceList.id), priceListName: priceList.name } : {}),
    }));
  };

  const handleApplyPriceList = async (priceListId) => {
    if (!priceListId) {
      setProposalData(prev => ({
        ...prev,
        priceListId: '',
        priceListName: '',
        items: (prev.items || []).map(item => ({ ...item, _priceListMissing: false })),
      }));
      return;
    }
    try {
      const plItems = await catalogService.priceLists.getItems(priceListId);
      const itemsArr = Array.isArray(plItems) ? plItems : (plItems?.results || []);
      const priceMap = {};
      itemsArr.forEach(pli => {
        priceMap[parseInt(pli.product)] = parseFloat(pli.price_fob_unit);
      });
      const pl = priceLists.find(p => String(p.id) === String(priceListId));
      setProposalData(prev => ({
        ...prev,
        priceListId: String(priceListId),
        priceListName: pl?.name || '',
        items: (prev.items || []).map(item => {
          const pid = item.product_id ? parseInt(item.product_id) : null;
          if (pid !== null && priceMap[pid] !== undefined) {
            const price = priceMap[pid];
            return { ...item, price_fob_unit: price, total: (parseInt(item.quantity) || 0) * price, _priceListMissing: false };
          }
          return { ...item, _priceListMissing: !!pid };
        }),
      }));
    } catch (e) {
      console.error('Error aplicando lista de precios:', e);
      alert('No se pudo cargar la lista de precios. Intente nuevamente.');
    }
  };

  const handleSelectAltPriceList = async (itemIndex, altPriceListId) => {
    const item = (proposalData.items || [])[itemIndex];
    if (!item?.product_id || !altPriceListId) return;
    try {
      const res = await catalogService.priceLists.getProductPrice(item.product_id, altPriceListId);
      if (res?.price_fob_unit !== null && res?.price_fob_unit !== undefined) {
        const price = parseFloat(res.price_fob_unit);
        handleUpdateItem(itemIndex, {
          price_fob_unit: price,
          total: (parseInt(item.quantity) || 0) * price,
          _priceListMissing: false,
        });
      } else {
        handleUpdateItem(itemIndex, { _priceListMissing: false });
        alert('El producto no tiene precio en la lista seleccionada. Ingrese el precio manualmente.');
      }
    } catch (e) {
      handleUpdateItem(itemIndex, { _priceListMissing: false });
      alert('El producto no tiene precio en la lista seleccionada. Ingrese el precio manualmente.');
    }
  };

  const handleConvertToOrder = async () => {
    if (!proposalData?.id) return;
    setConvertingOrder(true);
    try {
      const created = await customerOrderService.generateFromProposal(proposalData.id);
      setProposalData(prev => ({ ...prev, status: 'Convertida' }));
      setShowConvertConfirm(false);
      if (onGenerateCustomerOrder) {
        onGenerateCustomerOrder(created);
      }
    } catch (error) {
      console.error('Error generando Pedido desde cotización:', error);
      alert(`No se pudo generar el Pedido.\n${parseErrorMessage(error)}`);
    } finally {
      setConvertingOrder(false);
    }
  };

  const contact = getContact();
  const total = calculateProposalTotal();

  const PROPOSAL_SECTIONS = [
    { id: 'general', label: 'General' },
    { id: 'detalle', label: 'Detalle de la Cotización' },
  ];

  return (
    <>
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
      {/* Header compacto */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-800">
            {proposalData.proposalId || 'Nueva Cotización'}
          </h2>
          {contact && (
            <span className="text-sm text-gray-500">— {contact.name}</span>
          )}
          <span className="text-sm text-gray-400">
            Total: <span className="font-bold text-gray-700">{formatCurrency(total)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={proposalData.status}
            onChange={(e) => {
              if (e.target.value === 'Convertida') {
                setShowConvertConfirm(true);
              } else {
                setProposalData(prev => ({ ...prev, status: e.target.value }));
              }
            }}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="Borrador">Borrador</option>
            <option value="Enviada">Enviada</option>
            <option value="Respondida">Respondida</option>
            <option value="Convertida">Convertida a Pedido</option>
            <option value="Cancelada">Cancelada</option>
            <option value="Aprobada">Aprobada</option>
          </select>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium text-sm flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Tabs horizontales (mismo estilo que Producto) */}
      <div className="flex gap-1 px-6 pt-3 border-b border-gray-200">
        {PROPOSAL_SECTIONS.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeSection === section.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      <div className="p-6">
          {activeSection === 'general' && (
            <ProposalGeneralSection
              proposal={proposalData}
              contact={contact}
              contacts={contacts}
              priceLists={priceLists}
              onUpdate={(updates) => setProposalData(prev => ({ ...prev, ...updates }))}
            />
          )}

          {activeSection === 'detalle' && (
            <>
              <ProposalItemsTable
                items={proposalData.items || []}
                products={products}
                onAddItem={handleAddItem}
                onUpdateItem={handleUpdateItem}
                onDeleteItem={handleDeleteItem}
                onClearItems={handleClearItems}
                onProductSelect={handleProductSelect}
                onBulkAdd={handleBulkAddProducts}
                formatCurrency={formatCurrency}
                proposalId={proposalData.proposalId}
                priceLists={priceLists}
                selectedPriceListId={proposalData.priceListId || ''}
                onApplyPriceList={handleApplyPriceList}
                onSelectAltPriceList={handleSelectAltPriceList}
                theme={theme}
                userId={user?.id}
              />

              {/* ── Panel Logístico ─────────────────────────────────────── */}
              {logisticsSummary.hasData && (
                <div className="mt-4 border border-blue-100 bg-blue-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold text-blue-800">Resumen Logístico</span>
                    <span className="text-xs text-blue-500 font-normal ml-1">(estimado desde ítems)</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
                      <p className="text-xs text-gray-500 mb-0.5">Total piezas</p>
                      <p className="text-base font-bold text-gray-800">{logisticsSummary.totalPcs.toLocaleString('es-AR')}</p>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
                      <p className="text-xs text-gray-500 mb-0.5">Total cartones</p>
                      <p className="text-base font-bold text-gray-800">{logisticsSummary.totalCartons.toLocaleString('es-AR')}</p>
                    </div>
                    <div className="bg-white rounded-lg px-3 py-2 border border-blue-100">
                      <p className="text-xs text-gray-500 mb-0.5">Total CBM</p>
                      <p className="text-base font-bold text-gray-800">{logisticsSummary.totalCbm.toFixed(2)} m³</p>
                    </div>
                    <div className={`rounded-lg px-3 py-2 border ${logisticsSummary.suggestedContainer ? 'bg-green-50 border-green-200' : 'bg-white border-blue-100'}`}>
                      <p className="text-xs text-gray-500 mb-0.5">Contenedor sugerido</p>
                      {logisticsSummary.suggestedContainer ? (
                        <p className="text-base font-bold text-green-700">
                          {logisticsSummary.suggestedContainer.count > 1 ? `${logisticsSummary.suggestedContainer.count}×` : ''}
                          {logisticsSummary.suggestedContainer.spec.label}
                          <span className="text-xs font-normal text-gray-500 ml-1">({logisticsSummary.suggestedContainer.spec.cbm} m³)</span>
                        </p>
                      ) : (
                        <p className="text-sm text-gray-400">Sin datos CBM</p>
                      )}
                    </div>
                  </div>
                  {logisticsSummary.suggestedContainer && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-blue-600">Ocupación del contenedor sugerido</span>
                        <span className="font-semibold text-blue-700">
                          {Math.min(100, (logisticsSummary.totalCbm / (logisticsSummary.suggestedContainer.spec.cbm * logisticsSummary.suggestedContainer.count) * 100)).toFixed(1)}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (logisticsSummary.totalCbm / (logisticsSummary.suggestedContainer.spec.cbm * logisticsSummary.suggestedContainer.count)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
    </div>

    {/* Modal de confirmación: Convertir cotización a Pro Forma */}
    {showConvertConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200">
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
              <ShoppingCart className="w-7 h-7 text-purple-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-800">Convertir a Pro Forma</h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              ¿Usted está seguro de cambiar esta cotización en Pro forma?
              <br />
              <span className="font-semibold text-gray-800">Se generará un pedido automáticamente.</span>
            </p>
            <div className="flex gap-3 w-full pt-2">
              <button
                onClick={() => setShowConvertConfirm(false)}
                disabled={convertingOrder}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleConvertToOrder}
                disabled={convertingOrder}
                className="flex-1 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {convertingOrder ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generando...
                  </>
                ) : 'Aceptar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

// Componente para Nota de Comercial con modal de edición
const CommercialNoteSection = ({ proposal, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [noteText, setNoteText] = useState(proposal.commercial_note || '');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadHistory = async () => {
    if (!proposal.id) {
      console.warn('No se puede cargar historial: proposal.id no existe');
      return;
    }
    setLoadingHistory(true);
    try {
      const response = await proposalService.getCommercialNoteHistory(proposal.id);
      console.log('[COMMERCIAL NOTE] Respuesta del historial:', response);
      console.log('[COMMERCIAL NOTE] Tipo de respuesta:', typeof response, Array.isArray(response));
      
      // Asegurar que response sea un array
      const historyArray = Array.isArray(response) ? response : (response?.data || response || []);
      console.log('[COMMERCIAL NOTE] Historial procesado:', historyArray);
      setHistory(historyArray);
    } catch (error) {
      console.error('Error cargando historial:', error);
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenModal = async () => {
    console.log('handleOpenModal llamado', { proposalId: proposal.id, hasNote: !!proposal.commercial_note, showModal: false });
    if (!proposal.id) {
      console.error('No se puede abrir el modal: proposal.id no existe');
      alert('No se puede editar la nota: la cotización debe guardarse primero.');
      return;
    }
    setNoteText(proposal.commercial_note || '');
    setShowModal(true);
    // Cargar historial cuando se abre el modal
    await loadHistory();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await proposalService.updateCommercialNote(proposal.id, noteText);
      // El backend devuelve la propuesta actualizada con commercial_note
      // Mapear desde el formato del API al formato del frontend
      const updatedNote = response?.commercial_note || response?.commercialNote || noteText;
      onUpdate({ commercial_note: updatedNote });
      // Recargar historial para mostrar la nueva edición
      await loadHistory();
      setShowModal(false);
    } catch (error) {
      console.error('Error guardando nota comercial:', error);
      alert(`No se pudo guardar la nota comercial.\n${parseErrorMessage(error)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nota de Comercial:
            </label>
            <div 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenModal();
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              className="min-h-[60px] px-4 py-3 border border-gray-300 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-colors flex items-start gap-2"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpenModal();
                }
              }}
            >
              <MessageSquare className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
              <p className="text-sm text-gray-700 whitespace-pre-line flex-1">
                {proposal.commercial_note && proposal.commercial_note.trim() 
                  ? proposal.commercial_note 
                  : '(Sin notas especificadas)'}
              </p>
            </div>
            {proposal.commercial_note && (
              <p className="text-xs text-gray-500 mt-2">
                Haz clic para editar
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Modal de edición */}
      <AnimatePresence mode="wait">
        {showModal && (
          <CommercialNoteModal
            noteText={noteText}
            setNoteText={setNoteText}
            history={history}
            loadingHistory={loadingHistory}
            saving={saving}
            onSave={handleSave}
            onClose={() => {
              setShowModal(false);
            }}
          />
        )}
      </AnimatePresence>
      {/* Debug: mostrar estado del modal */}
      {process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-400 mt-2">
          Debug: showModal = {showModal ? 'true' : 'false'}, proposal.id = {proposal.id || 'undefined'}
        </div>
      )}
    </>
  );
};

// Modal de edición de Nota de Comercial
const CommercialNoteModal = ({ noteText, setNoteText, history, loadingHistory, saving, onSave, onClose }) => {
  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
      onClick={onClose}
    >
      <div
        className="w-full flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            Nota de Comercial
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Editor de texto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Texto de la nota
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Ingresa la nota comercial..."
              rows={8}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
            />
          </div>

          {/* Historial de ediciones */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-gray-600" />
              <h4 className="text-sm font-semibold text-gray-700">Historial de ediciones</h4>
            </div>
            
            {loadingHistory ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <p className="text-sm text-gray-500 mt-2">Cargando historial...</p>
              </div>
            ) : history.length > 0 ? (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {history.map((item, index) => (
                  <motion.div
                    key={item.id || index}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-gray-600 mb-1">
                          {item.user_name || 'Usuario desconocido'}
                          {item.user_email && (
                            <span className="text-gray-400 ml-2">({item.user_email})</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {format(new Date(item.created_at), 'dd/MM/yyyy HH:mm')}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-line mt-2">
                      {item.note_text || '(Sin texto)'}
                    </p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No hay historial de ediciones aún
              </p>
            )}
          </div>
        </div>

        {/* Botones */}
        <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Guardar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Sección de Datos del Cliente
// ─── Sección General (nuevo diseño) ───────────────────────────────────────────
const ProposalGeneralSection = ({ proposal, contact, contacts, priceLists = [], onUpdate }) => {
  const [searchContact, setSearchContact] = useState(contact?.name || '');
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const contactRef = useRef(null);

  useEffect(() => {
    if (contact?.name) setSearchContact(contact.name);
  }, [contact?.id]);

  useEffect(() => {
    const handler = (e) => {
      if (contactRef.current && !contactRef.current.contains(e.target)) setShowContactDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredContacts = useMemo(() => {
    if (!showContactDropdown) return [];
    if (!searchContact.trim()) return contacts.slice(0, 50);
    const q = searchContact.toLowerCase();
    return contacts.filter(c => c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)).slice(0, 50);
  }, [contacts, searchContact, showContactDropdown]);

  const inputCls = 'w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500';
  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

  const buildContactDefaults = useCallback(
    (selectedContact) => getContactCommercialDefaults(selectedContact, priceLists),
    [priceLists]
  );

  const applyContactDefaults = (selectedContact) => {
    const updates = buildContactDefaults(selectedContact);
    if (Object.keys(updates).length > 0) onUpdate(updates);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h3 className="text-base font-bold text-gray-800 border-b border-gray-200 pb-2">General</h3>

      {/* Estado */}
      <div>
        <label className={labelCls}>Estado de la cotización</label>
        <select
          value={proposal.status || 'Borrador'}
          onChange={(e) => onUpdate({ status: e.target.value })}
          className={inputCls}
        >
          <option value="Borrador">Borrador</option>
          <option value="Enviada">Enviada</option>
          <option value="Respondida">Respondida</option>
          <option value="Aprobada">Aprobada</option>
          <option value="Cancelada">Cancelada</option>
        </select>
      </div>

      {/* Cliente */}
      <div>
        <label className={labelCls}>Cliente</label>
        <div className="relative" ref={contactRef}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchContact}
            onChange={(e) => { setSearchContact(e.target.value); setShowContactDropdown(true); if (!e.target.value) onUpdate({ contactId: null }); }}
            onFocus={() => setShowContactDropdown(true)}
            placeholder="Buscar contacto..."
            className={`${inputCls} pl-9`}
          />
          {showContactDropdown && filteredContacts.length > 0 && (
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
              {filteredContacts.map(c => (
                <button key={c.id} type="button"
                  onClick={() => { onUpdate({ contactId: c.id, ...buildContactDefaults(c) }); setSearchContact(c.name); setShowContactDropdown(false); }}
                  onMouseDown={(e) => e.preventDefault()}
                  className="w-full px-4 py-2.5 text-left hover:bg-blue-50 flex items-center gap-3 border-b border-gray-50 last:border-0">
                  <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <span className="text-xs font-bold text-blue-700">{(c.name||'?')[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{c.name}</div>
                    {c.email && <div className="text-xs text-gray-500 truncate">{c.email}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {contact && (
          <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <User className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-medium">{contact.name}</span>
            {contact.email && <span className="text-gray-400">· {contact.email}</span>}
            <button
              type="button"
              onClick={() => applyContactDefaults(contact)}
              className="ml-auto text-xs font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Traer datos del contacto
            </button>
          </div>
        )}
      </div>

      {/* Lista de precios */}
      <div>
        <label className={labelCls}>Lista de precios</label>
        <select
          value={proposal.priceListId || ''}
          onChange={(e) => onUpdate({ priceListId: e.target.value || null })}
          className={inputCls}
        >
          <option value="">Sin lista de precios</option>
          {priceLists.map(pl => (
            <option key={pl.id} value={String(pl.id)}>{pl.name}</option>
          ))}
        </select>
      </div>

      {/* Término de pago + Fecha */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Término de pago</label>
          <input
            type="text"
            placeholder="Ej: 30 días, contado..."
            value={proposal.paymentTerms || ''}
            onChange={(e) => onUpdate({ paymentTerms: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Fecha de cotización</label>
          <input
            type="date"
            value={proposal.createdAt ? new Date(proposal.createdAt).toISOString().slice(0, 10) : ''}
            readOnly
            className={`${inputCls} bg-gray-50 cursor-default`}
          />
        </div>
      </div>

      {/* Rebaja */}
      <div>
        <label className={labelCls}>Rebaja (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          step="0.01"
          placeholder="0.00"
          value={proposal.rebate ?? ''}
          onChange={(e) => onUpdate({ rebate: e.target.value })}
          className={inputCls}
        />
      </div>

      {/* Otros comentarios */}
      <div>
        <label className={labelCls}>Otros comentarios</label>
        <textarea
          rows={4}
          placeholder="Notas adicionales, condiciones especiales..."
          value={proposal.commercial_note || ''}
          onChange={(e) => onUpdate({ commercial_note: e.target.value })}
          className={`${inputCls} resize-none`}
        />
      </div>
    </div>
  );
};

// ─── ProposalHeader (legado, se mantiene para compatibilidad) ──────────────────
const ProposalHeader = ({ proposal, contact, contacts, companies = [], onUpdate }) => {
  const [searchContact, setSearchContact] = useState(contact?.name || '');
  const [showContactSearch, setShowContactSearch] = useState(false);
  const contactSearchRef = useRef(null);

  useEffect(() => {
    if (contact?.name) {
      setSearchContact(contact.name);
    }
  }, [contact?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contactSearchRef.current && !contactSearchRef.current.contains(event.target)) {
        setShowContactSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Contactos filtrados por empresa seleccionada
  const contactsByCompany = useMemo(() => {
    if (!proposal.companyId) return contacts;
    return contacts.filter(c => {
      const companyIds = Array.isArray(c.companies) ? c.companies.map((company) => String(company.id)) : [];
      return companyIds.includes(String(proposal.companyId));
    });
  }, [contacts, proposal.companyId]);

  const filteredContacts = useMemo(() => {
    if (!showContactSearch) return [];
    if (!searchContact.trim()) return contactsByCompany.slice(0, 50);
    const searchLower = searchContact.toLowerCase();
    return contactsByCompany.filter(c =>
      c.name?.toLowerCase().includes(searchLower) ||
      c.email?.toLowerCase().includes(searchLower)
    ).slice(0, 50);
  }, [contactsByCompany, searchContact, showContactSearch]);

  const handleContactSelect = (selectedContact) => {
    onUpdate({ contactId: selectedContact.id });
    setSearchContact(selectedContact.name);
    setShowContactSearch(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Datos del Cliente</h3>

        {/* Selector de Empresa ocultado */}

        {/* Selector de Contacto */}
        <div className="mb-4" ref={contactSearchRef}>
          <label className="block text-sm font-medium text-gray-700 mb-2">Contacto</label>
          <div className="relative">
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchContact}
                onChange={(e) => { setSearchContact(e.target.value); setShowContactSearch(true); }}
                onFocus={() => setShowContactSearch(true)}
                placeholder="Buscar contacto..."
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
            {showContactSearch && (
              <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredContacts.length > 0 ? (
                  filteredContacts.map(c => (
                    <button key={c.id} type="button" onClick={() => handleContactSelect(c)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors">
                      <User className="w-4 h-4 text-gray-400" />
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{c.name}</div>
                        {c.email && <div className="text-sm text-gray-500">{c.email}</div>}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-center text-gray-500 text-sm">No se encontraron contactos</div>
                )}
              </div>
            )}
          </div>
        </div>

        {contact && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
              <input type="text" value={contact.name || ''} readOnly className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tratamiento</label>
              <select value={proposal.salutation || 'Sr.'} onChange={(e) => onUpdate({ salutation: e.target.value })}
                className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700">
                <option value="Sr.">Sr.</option>
                <option value="Sra.">Sra.</option>
                <option value="Sres.">Sres.</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={contact.email || ''} readOnly className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
              <input type="tel" value={contact.phone || ''} readOnly className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-600" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
              <input type="text" value={contact.position || ''} readOnly className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-600" />
            </div>
          </div>
        )}

      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Saludo inicial</label>
        <textarea
          value={proposal.greeting || ''}
          onChange={(e) => onUpdate({ greeting: e.target.value })}
          placeholder="Ej: Estimado/a [Nombre], nos complace presentarle nuestra cotización..."
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Condición de pago</label>
          <input
            type="text"
            value={proposal.paymentTerms || ''}
            onChange={(e) => onUpdate({ paymentTerms: e.target.value })}
            placeholder="Ej: 30 días, contado, etc."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Rebate %</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={proposal.rebate ?? ''}
            onChange={(e) => onUpdate({ rebate: e.target.value })}
            placeholder="0.00"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Nota de Comercial */}
      <CommercialNoteSection proposal={proposal} onUpdate={onUpdate} />
    </div>
  );
};

// ─── Modal de selección de campos para exportar Excel ───
const ExportFieldsModal = ({ fields, onClose, onExport }) => {
  const [selected, setSelected] = useState(() =>
    Object.fromEntries(fields.map(f => [f.key, f.default]))
  );

  const toggle = (key) => setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  const selectAll = () => setSelected(Object.fromEntries(fields.map(f => [f.key, true])));
  const clearAll  = () => setSelected(Object.fromEntries(fields.map(f => [f.key, false])));

  const count = Object.values(selected).filter(Boolean).length;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Download className="w-5 h-5 text-green-600" />
              Exportar a Excel
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">Seleccioná las columnas que querés exportar</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Acciones rápidas */}
        <div className="flex gap-2 px-6 pt-4">
          <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-800 font-medium">
            Seleccionar todo
          </button>
          <span className="text-gray-300">|</span>
          <button onClick={clearAll} className="text-xs text-gray-500 hover:text-gray-700 font-medium">
            Limpiar selección
          </button>
        </div>

        {/* Lista de campos */}
        <div className="px-6 py-4 space-y-2 max-h-80 overflow-y-auto">
          {fields.map(f => (
            <label
              key={f.key}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors border ${
                selected[f.key]
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              }`}
            >
              <input
                type="checkbox"
                checked={!!selected[f.key]}
                onChange={() => toggle(f.key)}
                className="w-4 h-4 text-green-600 rounded border-gray-300 flex-shrink-0"
              />
              <span className={`text-sm font-medium ${selected[f.key] ? 'text-green-800' : 'text-gray-700'}`}>
                {f.label}
              </span>
              {f.key === 'img' && (
                <span className="text-xs text-gray-400 ml-auto">incluye foto del producto</span>
              )}
              {f.isCurrency && (
                <span className="text-xs text-gray-400 ml-auto">USD</span>
              )}
            </label>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500">{count} columna(s) seleccionada(s)</span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={() => count > 0 && onExport(selected)}
              disabled={count === 0}
              className="px-5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Selección Masiva de Productos integrada en la cotización ───
const BulkProductSelector = ({ products, onAddProducts, formatCurrency }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [brandTypeFilter, setBrandTypeFilter] = useState('all');
  const [lineFilter, setLineFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [factoryFilter, setFactoryFilter] = useState('all');
  const [priceListFilter, setPriceListFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState({});
  const [showFilters, setShowFilters] = useState(true);

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [priceLists, setPriceLists] = useState([]);

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [cats, brs, lines, tags, pls] = await Promise.all([
          categoryService.getFlat({ active_only: true }),
          catalogService.brands.getAll({ active_only: true }),
          catalogService.productLines.getAll({ active_only: true }),
          catalogService.productTags.getAll({ active_only: true }),
          catalogService.priceLists.getAll({ active_only: true }),
        ]);
        setCategories(Array.isArray(cats) ? cats : []);
        setBrands(Array.isArray(brs) ? brs : []);
        setProductLines(Array.isArray(lines) ? lines : []);
        setProductTags(Array.isArray(tags) ? tags : []);
        setPriceLists(Array.isArray(pls) ? pls : []);
      } catch (err) {
        console.error('Error cargando catálogos:', err);
      }
    };
    loadCatalogs();
  }, []);

  const factories = useMemo(() => {
    const map = {};
    products.forEach(p => {
      if (p.factory_name) map[p.factory_name] = true;
    });
    return Object.keys(map).sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        const match = (p.name || '').toLowerCase().includes(s) ||
          (p.product_code || '').toLowerCase().includes(s) ||
          (p.description || '').toLowerCase().includes(s);
        if (!match) return false;
      }
      if (categoryFilter !== 'all' && String(p.category_id) !== String(categoryFilter)) return false;
      if (brandFilter !== 'all' && String(p.brand_id) !== String(brandFilter)) return false;
      if (brandTypeFilter !== 'all' && (p.brand_type || 'GENERIC') !== brandTypeFilter) return false;
      if (lineFilter !== 'all' && String(p.product_line_id) !== String(lineFilter)) return false;
      if (tagFilter !== 'all') {
        const tagIds = (p.tag_ids || []).map(String);
        if (!tagIds.includes(String(tagFilter))) return false;
      }
      if (factoryFilter !== 'all' && (p.factory_name || '') !== factoryFilter) return false;
      if (statusFilter !== 'all' && String(p.is_active ?? '') !== statusFilter) return false;
      return true;
    });
  }, [products, searchTerm, categoryFilter, brandFilter, brandTypeFilter, lineFilter, tagFilter, factoryFilter, statusFilter]);

  const selectProducts = (productsToSelect) => {
    setSelectedProducts(prev => {
      const next = { ...prev };
      productsToSelect.forEach(product => {
        if (!next[product.id]) next[product.id] = { ...product, quantity: product.moq || 1 };
      });
      return next;
    });
  };

  const toggleProduct = (product) => {
    setSelectedProducts(prev => {
      const next = { ...prev };
      if (next[product.id]) {
        delete next[product.id];
      } else {
        next[product.id] = { ...product, quantity: product.moq || 1 };
      }
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected = filteredProducts.every(p => selectedProducts[p.id]);
    if (allSelected) {
      const next = { ...selectedProducts };
      filteredProducts.forEach(p => delete next[p.id]);
      setSelectedProducts(next);
    } else {
      selectProducts(filteredProducts);
    }
  };

  const selectBrand = () => {
    if (brandFilter === 'all') return;
    setSearchTerm('');
    setCategoryFilter('all');
    setBrandTypeFilter('all');
    setLineFilter('all');
    setTagFilter('all');
    setFactoryFilter('all');
    setStatusFilter('all');
    selectProducts(products.filter(p => String(p.brand_id) === String(brandFilter)));
  };

  const selectBrandType = (brandType) => {
    setSearchTerm('');
    setCategoryFilter('all');
    setBrandFilter('all');
    setBrandTypeFilter(brandType);
    setLineFilter('all');
    setTagFilter('all');
    setFactoryFilter('all');
    setStatusFilter('all');
    selectProducts(products.filter(p => (p.brand_type || 'GENERIC') === brandType));
  };

  const updateQuantity = (productId, qty) => {
    setSelectedProducts(prev => ({
      ...prev,
      [productId]: { ...prev[productId], quantity: parseInt(qty) || 1 }
    }));
  };

  const handleConfirm = () => {
    const selected = Object.values(selectedProducts);
    if (selected.length === 0) return;

    const selectedPriceList = priceListFilter !== 'all'
      ? priceLists.find(pl => String(pl.id) === String(priceListFilter))
      : null;

    onAddProducts(selected, selectedPriceList);
    setSelectedProducts({});
  };

  const selectedCount = Object.keys(selectedProducts).length;
  const allFiltered = filteredProducts.every(p => selectedProducts[p.id]) && filteredProducts.length > 0;

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setBrandFilter('all');
    setBrandTypeFilter('all');
    setLineFilter('all');
    setTagFilter('all');
    setFactoryFilter('all');
    setStatusFilter('all');
  };

  return (
    <div className="bg-white border border-purple-200 rounded-xl shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-purple-100 bg-purple-50">
          <div>
            <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-purple-600" />
              Selección Masiva de Productos
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedCount > 0
                ? `${selectedCount} producto(s) seleccionado(s)`
                : 'Seleccioná los productos que querés agregar a la cotización'}
            </p>
          </div>
        </div>

        {/* Lista de precios + Filtros */}
        <div className="px-4 py-3 border-b border-gray-100 space-y-3">
          {/* Lista de precios */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Lista de Precios:</label>
            <select
              value={priceListFilter}
              onChange={(e) => setPriceListFilter(e.target.value)}
              className="flex-1 max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            >
              <option value="all">Precio del catálogo (por defecto)</option>
              {priceLists.map(pl => (
                <option key={pl.id} value={pl.id}>{pl.name} {pl.code ? `(${pl.code})` : ''} — {pl.currency || 'USD'}</option>
              ))}
            </select>
          </div>

          {/* Toggle filtros */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              <Filter className="w-4 h-4" />
              {showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            </button>
            {showFilters && (
              <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-700">
                Limpiar filtros
              </button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Búsqueda */}
              <div className="col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nombre, código o descripción..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                  />
                </div>
              </div>
              {/* Categoría */}
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Todas las categorías</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.full_path || c.name}</option>
                ))}
              </select>
              {/* Marca */}
              <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Todas las marcas</option>
                {brands.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {/* Tipo de marca */}
              <select value={brandTypeFilter} onChange={(e) => setBrandTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Orilat y genéricos</option>
                <option value="OWN">Solo Orilat</option>
                <option value="GENERIC">Solo genéricos</option>
              </select>
              {/* Línea */}
              <select value={lineFilter} onChange={(e) => setLineFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Todas las líneas</option>
                {productLines.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              {/* Tag */}
              <select value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Todos los tags</option>
                {productTags.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {/* Fábrica */}
              <select value={factoryFilter} onChange={(e) => setFactoryFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Todas las fábricas</option>
                {factories.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              {/* Estado */}
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/30">
                <option value="all">Todos los estados</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">Selección rápida:</span>
            <button type="button" onClick={selectBrand} disabled={brandFilter === 'all'}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed">
              Seleccionar marca elegida
            </button>
            <button type="button" onClick={() => selectBrandType('OWN')}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100">
              Todos los productos Orilat
            </button>
            <button type="button" onClick={() => selectBrandType('GENERIC')}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100">
              Todos los productos genéricos
            </button>
            {selectedCount > 0 && (
              <button type="button" onClick={() => setSelectedProducts({})}
                className="px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50">
                Quitar selección
              </button>
            )}
          </div>
        </div>

        {/* Tabla de productos */}
        <div className="max-h-[28rem] overflow-auto px-4">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="border-b border-gray-200">
                <th className="py-3 px-2 text-left w-10">
                  <input type="checkbox" checked={allFiltered} onChange={toggleAll}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                </th>
                <th className="py-3 px-2 text-left w-14">Foto</th>
                <th className="py-3 px-2 text-left">Producto</th>
                <th className="py-3 px-2 text-left">Código</th>
                <th className="py-3 px-2 text-left">Marca</th>
                <th className="py-3 px-2 text-left">Categoría</th>
                <th className="py-3 px-2 text-left">Fábrica</th>
                <th className="py-3 px-2 text-right">Precio FOB</th>
                <th className="py-3 px-2 text-center">MOQ</th>
                <th className="py-3 px-2 text-center w-24">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="10" className="py-12 text-center text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No se encontraron productos con los filtros aplicados</p>
                  </td>
                </tr>
              ) : (
                filteredProducts.map(product => {
                  const isSelected = !!selectedProducts[product.id];
                  return (
                    <tr key={product.id}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => toggleProduct(product)}>
                      <td className="py-2 px-2">
                        <input type="checkbox" checked={isSelected} readOnly
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 pointer-events-none" />
                      </td>
                      <td className="py-2 px-2">
                        {product.main_image ? (
                          <img src={product.main_image} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Image className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2 font-medium text-gray-800 max-w-[200px] truncate">{product.name}</td>
                      <td className="py-2 px-2 text-gray-600">{product.product_code || '-'}</td>
                      <td className="py-2 px-2 text-gray-600">{product.brand_name || '-'}</td>
                      <td className="py-2 px-2 text-gray-600 max-w-[120px] truncate">{product.category || '-'}</td>
                      <td className="py-2 px-2 text-gray-600">{product.factory_name || '-'}</td>
                      <td className="py-2 px-2 text-right font-medium text-green-700">{formatCurrency(product.price_fob || 0)}</td>
                      <td className="py-2 px-2 text-center text-gray-600">{product.moq || '-'}</td>
                      <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                        {isSelected && (
                          <input
                            type="number"
                            min="1"
                            value={selectedProducts[product.id]?.quantity || 1}
                            onChange={(e) => updateQuantity(product.id, e.target.value)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-sm text-center focus:ring-2 focus:ring-blue-500/30"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-3 bg-gray-50 flex-wrap">
          <div className="text-sm text-gray-600">
            Mostrando {filteredProducts.length} producto(s) — {selectedCount} seleccionado(s)
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <button onClick={handleConfirm} disabled={selectedCount === 0}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              <Plus className="w-4 h-4" />
              Agregar {selectedCount} producto(s)
            </button>
          </div>
        </div>
    </div>
  );
};

// ─── Tabla de Items Estructurados ───
// Definición global de todas las columnas del detalle
const DETAIL_COLUMNS = [
  { key: 'product_image',       label: 'Imagen',              defaultVisible: true,  editable: false, width: 'w-16'   },
  { key: 'product_code',        label: 'Cód. Artículo',       defaultVisible: true,  editable: false, width: 'w-28'   },
  { key: 'product_name',        label: 'Producto',            defaultVisible: true,  editable: true,  width: 'w-52'   },
  { key: 'product_variant',     label: 'Variante',            defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'brand_name',          label: 'Marca',               defaultVisible: true,  editable: false, width: 'w-24'   },
  { key: 'category_name',       label: 'Categoría',           defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'description',         label: 'Descripción',         defaultVisible: false, editable: false, width: 'w-40'   },
  { key: 'link_web',            label: 'Link web',            defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'packaging_type_name', label: 'Tipo Embalaje',       defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'quantity',            label: 'Cant. Pedida',        defaultVisible: true,  editable: true,  width: 'w-24'   },
  { key: 'price_fob_unit',      label: 'Precio FOB Unit.',    defaultVisible: true,  editable: true,  width: 'w-28'   },
  { key: 'total',               label: 'Total',               defaultVisible: true,  editable: false, width: 'w-28'   },
  { key: 'product_size',        label: 'Tamaño producto',     defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'packaging_height',    label: 'Alt. Embalaje',       defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'packaging_length',    label: 'Long. Embalaje',      defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'packaging_width',     label: 'Ancho Embalaje',      defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'master_pack_height',  label: 'Alt. Master Pack',    defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'master_pack_length',  label: 'Long. Master Pack',   defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'master_pack_width',   label: 'Ancho Master Pack',   defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'master_pack_gw',      label: 'Peso Bruto MP',       defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'master_pack_nw',      label: 'Peso Neto MP',        defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'pieces',              label: 'Piezas',              defaultVisible: false, editable: false, width: 'w-20'   },
  { key: 'pcs_per_ctn',         label: 'PCS/CTN',             defaultVisible: true,  editable: false, width: 'w-20'   },
  { key: 'inners',              label: 'Inners',              defaultVisible: false, editable: false, width: 'w-20'   },
  { key: 'cbm_per_ctn',         label: 'CBM/CTN',             defaultVisible: true,  editable: false, width: 'w-20'   },
  { key: 'moq',                 label: 'MOQ',                 defaultVisible: true,  editable: true,  width: 'w-20'   },
  { key: 'moq_oem',             label: 'MOQ OEM',             defaultVisible: false, editable: false, width: 'w-20'   },
  { key: 'port_name',           label: 'Puerto',              defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'battery_info',        label: 'Info Batería',        defaultVisible: false, editable: false, width: 'w-32'   },
  { key: 'recommended_age',     label: 'Edad Recomendada',    defaultVisible: false, editable: false, width: 'w-24'   },
  { key: 'material_composition',label: 'Composición',         defaultVisible: false, editable: false, width: 'w-32'   },
  { key: 'manufacturer_code',   label: 'Cód. Fábrica',        defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'assortment',          label: 'Surtido',             defaultVisible: false, editable: false, width: 'w-28'   },
  { key: 'discount_fob',        label: 'Descuento FOB',       defaultVisible: false, editable: false, width: 'w-24'   },
];

const ProposalItemsTable = ({ items, products, onAddItem, onUpdateItem, onDeleteItem, onClearItems, onProductSelect, onBulkAdd, formatCurrency, proposalId, priceLists = [], selectedPriceListId = '', onApplyPriceList, onSelectAltPriceList, theme = 'light', userId }) => {
  const isDark = theme === 'dark';
  const [showExportModal, setShowExportModal] = useState(false);
  const [applyingPriceList, setApplyingPriceList] = useState(false);
  const [pendingPriceListId, setPendingPriceListId] = useState(selectedPriceListId || '');
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [columnSaved, setColumnSaved] = useState(false);

  const colStorageKey = userId ? `proposal_columns_${userId}` : 'proposal_columns_default';

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(colStorageKey);
      if (saved) return JSON.parse(saved);
    } catch (_) {}
    return Object.fromEntries(DETAIL_COLUMNS.map(c => [c.key, c.defaultVisible]));
  });

  const handleSaveColumns = () => {
    try {
      localStorage.setItem(colStorageKey, JSON.stringify(visibleColumns));
      setColumnSaved(true);
      setTimeout(() => setColumnSaved(false), 2000);
    } catch (_) {}
    setShowColumnPicker(false);
  };
  const grandTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

  useEffect(() => {
    setPendingPriceListId(selectedPriceListId || '');
  }, [selectedPriceListId]);

  const missingPriceCount = selectedPriceListId
    ? items.filter(i => i._priceListMissing && i.product_id).length
    : 0;

  const handleApplyClick = async () => {
    if (!onApplyPriceList) return;
    setApplyingPriceList(true);
    await onApplyPriceList(pendingPriceListId || null);
    setApplyingPriceList(false);
  };

  // Definición de todos los campos exportables
  const EXPORT_FIELDS = [
    { key: 'img',                  label: 'Imagen',              default: true,  width: 38, isCurrency: false },
    { key: 'product_code',         label: 'Cód. Artículo',       default: true,  width: 16, isCurrency: false },
    { key: 'product_name',         label: 'Producto',            default: true,  width: 32, isCurrency: false },
    { key: 'product_variant',      label: 'Variante',            default: false, width: 20, isCurrency: false },
    { key: 'brand_name',           label: 'Marca',               default: true,  width: 16, isCurrency: false },
    { key: 'category_name',        label: 'Categoría',           default: false, width: 20, isCurrency: false },
    { key: 'description',          label: 'Descripción',         default: false, width: 40, isCurrency: false },
    { key: 'link_web',             label: 'Link web',            default: false, width: 28, isCurrency: false },
    { key: 'packaging_type_name',  label: 'Tipo Embalaje',       default: false, width: 18, isCurrency: false },
    { key: 'quantity',             label: 'Cant. Pedida',        default: true,  width: 10, isCurrency: false },
    { key: 'price_fob_unit',       label: 'Precio FOB Unit.',    default: true,  width: 18, isCurrency: true  },
    { key: 'total',                label: 'Total (USD)',          default: true,  width: 16, isCurrency: true  },
    { key: 'product_size',         label: 'Tamaño producto',     default: false, width: 20, isCurrency: false },
    { key: 'packaging_height',     label: 'Alt. Embalaje (cm)',  default: false, width: 16, isCurrency: false },
    { key: 'packaging_length',     label: 'Long. Embalaje (cm)', default: false, width: 16, isCurrency: false },
    { key: 'packaging_width',      label: 'Ancho Embalaje (cm)', default: false, width: 16, isCurrency: false },
    { key: 'master_pack_height',   label: 'Alt. Master Pack',    default: false, width: 16, isCurrency: false },
    { key: 'master_pack_length',   label: 'Long. Master Pack',   default: false, width: 16, isCurrency: false },
    { key: 'master_pack_width',    label: 'Ancho Master Pack',   default: false, width: 16, isCurrency: false },
    { key: 'master_pack_gw',       label: 'Peso Bruto MP (kg)',  default: false, width: 14, isCurrency: false },
    { key: 'master_pack_nw',       label: 'Peso Neto MP (kg)',   default: false, width: 14, isCurrency: false },
    { key: 'pieces',               label: 'Piezas',              default: false, width: 10, isCurrency: false },
    { key: 'pcs_per_ctn',          label: 'PCS/CTN',             default: true,  width: 10, isCurrency: false },
    { key: 'inners',               label: 'Inners',              default: false, width: 10, isCurrency: false },
    { key: 'cbm_per_ctn',          label: 'CBM/CTN',             default: true,  width: 10, isCurrency: false },
    { key: 'moq',                  label: 'MOQ',                 default: true,  width: 10, isCurrency: false },
    { key: 'moq_oem',              label: 'MOQ OEM',             default: false, width: 10, isCurrency: false },
    { key: 'port_name',            label: 'Puerto',              default: false, width: 16, isCurrency: false },
    { key: 'battery_info',         label: 'Info Batería',        default: false, width: 20, isCurrency: false },
    { key: 'recommended_age',      label: 'Edad Recomendada',    default: false, width: 16, isCurrency: false },
    { key: 'material_composition', label: 'Composición',         default: false, width: 24, isCurrency: false },
    { key: 'manufacturer_code',    label: 'Cód. Fábrica',        default: false, width: 16, isCurrency: false },
    { key: 'assortment',           label: 'Surtido',             default: false, width: 16, isCurrency: false },
    { key: 'discount_fob',         label: 'Descuento FOB',       default: false, width: 14, isCurrency: true  },
  ];

  const handleExportExcel = async (selectedFields) => {
    if (items.length === 0) return;

    // Solo exportar las columnas seleccionadas
    const activeFields = EXPORT_FIELDS.filter(f => selectedFields[f.key]);
    const includesImg = activeFields.some(f => f.key === 'img');

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Cotización');

    ws.columns = activeFields.map(f => ({ header: f.label, key: f.key, width: f.width }));

    // Estilo del encabezado
    const headerRow = ws.getRow(1);
    headerRow.height = 22;
    headerRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E40AF' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFB0C4DE' } },
        bottom: { style: 'thin', color: { argb: 'FFB0C4DE' } },
        left: { style: 'thin', color: { argb: 'FFB0C4DE' } },
        right: { style: 'thin', color: { argb: 'FFB0C4DE' } },
      };
    });

    const IMG_ROW_HEIGHT = 160;
    const DATA_START_ROW = 2;

    // Índice 0-based de la columna imagen en las columnas activas
    const imgColIndex = activeFields.findIndex(f => f.key === 'img');

    // Helper: convierte una imagen (base64 o URL) a base64 puro
    const toBase64 = async (src) => {
      if (!src) return null;
      try {
        if (src.startsWith('data:image/')) {
          const [header, data] = src.split(',');
          const ext = header.match(/image\/(\w+)/)?.[1] || 'png';
          return { data, ext };
        }
        const res = await fetch(src);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => {
            const [header, data] = reader.result.split(',');
            const ext = header.match(/image\/(\w+)/)?.[1] || 'png';
            resolve({ data, ext });
          };
          reader.onerror = () => resolve(null);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    // Mapa de key → valor del item
    const getItemValue = (key, item) => {
      if (key === 'img') return '';
      const CURRENCY_KEYS = ['price_fob_unit', 'total', 'discount_fob'];
      const NUM_KEYS = [
        'quantity', 'pieces', 'pcs_per_ctn', 'inners', 'moq', 'moq_oem',
        'packaging_height', 'packaging_length', 'packaging_width',
        'master_pack_height', 'master_pack_length', 'master_pack_width',
        'master_pack_gw', 'master_pack_nw', 'cbm_per_ctn',
      ];
      if (CURRENCY_KEYS.includes(key)) return parseFloat(item[key]) || 0;
      if (NUM_KEYS.includes(key)) return parseFloat(item[key]) || '';
      return item[key] || '';
    };

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const rowIndex = DATA_START_ROW + i;
      const row = ws.getRow(rowIndex);
      row.height = includesImg ? IMG_ROW_HEIGHT : 20;

      activeFields.forEach(f => {
        const cell = row.getCell(f.key);
        cell.value = getItemValue(f.key, item);
        if (f.isCurrency) cell.numFmt = '"USD "$#,##0.00';
      });

      // Estilos de fila
      const fillColor = i % 2 === 0 ? 'FFF0F4FF' : 'FFFFFFFF';
      row.eachCell({ includeEmpty: true }, cell => {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        };
      });
      if (selectedFields['product_name']) {
        row.getCell('product_name').alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      }
      if (selectedFields['description']) {
        row.getCell('description').alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
      }

      // Insertar hasta 4 imágenes en grilla 2×2 en la columna "Fotos"
      if (includesImg) {
        const allImages = (item.product_images && item.product_images.length > 0)
          ? item.product_images
          : (item.product_image ? [item.product_image] : []);

        // Posiciones 2×2: [col_offset, row_offset] para cada slot
        const slots = [
          [0, 0],       // top-left
          [0.5, 0],     // top-right
          [0, 0.5],     // bottom-left
          [0.5, 0.5],   // bottom-right
        ];

        for (let imgIdx = 0; imgIdx < Math.min(allImages.length, 4); imgIdx++) {
          const imgSrc = allImages[imgIdx];
          if (!imgSrc) continue;
          const imgData = await toBase64(imgSrc);
          if (!imgData) continue;
          const validExts = ['png', 'jpeg', 'jpg', 'gif', 'bmp', 'webp'];
          const ext = validExts.includes(imgData.ext) ? imgData.ext : 'png';
          const excelExt = ext === 'jpg' ? 'jpeg' : ext;
          const [colOff, rowOff] = slots[imgIdx];
          try {
            const imageId = wb.addImage({ base64: imgData.data, extension: excelExt });
            ws.addImage(imageId, {
              tl: { col: imgColIndex + colOff,       row: rowIndex - 1 + rowOff },
              br: { col: imgColIndex + colOff + 0.5, row: rowIndex - 1 + rowOff + 0.5 },
              editAs: 'oneCell',
            });
          } catch (imgErr) {
            console.warn('No se pudo embeber imagen:', imgErr);
          }
        }
      }

      row.commit();
    }

    // Fila de total (solo si se incluye cantidad o total)
    const hasTotalCol = selectedFields['total'];
    const hasQtyCol = selectedFields['quantity'];
    if (hasTotalCol || hasQtyCol) {
      const totalRowIndex = DATA_START_ROW + items.length;
      const totalRow = ws.getRow(totalRowIndex);
      totalRow.height = 24;
      if (hasQtyCol) { totalRow.getCell('quantity').value = 'TOTAL'; }
      if (hasTotalCol) {
        totalRow.getCell('total').value = grandTotal;
        totalRow.getCell('total').numFmt = '"USD "$#,##0.00';
      }
      totalRow.eachCell({ includeEmpty: true }, cell => {
        cell.font = { bold: true, size: 12 };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } };
        cell.border = {
          top: { style: 'medium', color: { argb: 'FF059669' } },
          bottom: { style: 'medium', color: { argb: 'FF059669' } },
          left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
          right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        };
      });
      totalRow.commit();
    }

    // Generar y descargar
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Cotizacion_${proposalId || 'nueva'}_${format(new Date(), 'yyyyMMdd')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeColumns = DETAIL_COLUMNS.filter(c => visibleColumns[c.key]);
  const availableBulkProducts = useMemo(() => {
    const quotedProductIds = new Set(items.map(item => String(item.product_id)).filter(Boolean));
    return products.filter(product => !quotedProductIds.has(String(product.id)));
  }, [items, products]);

  return (
    <div className="space-y-4">
      {/* Barra de acciones */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-bold text-gray-800 border-b border-gray-200 pb-2 w-full">Detalle de la Cotización</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Selector de columnas */}
          <div className="relative">
            <button
              onClick={() => setShowColumnPicker(v => !v)}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Columnas
            </button>
            {showColumnPicker && (
              <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl w-64">
                <div className="flex items-center justify-between px-3 pt-3 pb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Columnas visibles</span>
                  <button
                    onClick={handleSaveColumns}
                    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md transition-colors flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" /> OK
                  </button>
                </div>
                <div className="flex gap-2 px-3 pb-2">
                  <button
                    onClick={() => setVisibleColumns(Object.fromEntries(DETAIL_COLUMNS.map(c => [c.key, true])))}
                    className="flex-1 text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium transition-colors"
                  >
                    Todas
                  </button>
                  <button
                    onClick={() => setVisibleColumns(Object.fromEntries(DETAIL_COLUMNS.map(c => [c.key, false])))}
                    className="flex-1 text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200 font-medium transition-colors"
                  >
                    Ninguna
                  </button>
                </div>
                <div className="border-t border-gray-100 px-3 pb-3 pt-2 space-y-1 max-h-72 overflow-y-auto">
                  {DETAIL_COLUMNS.map(col => (
                    <label key={col.key} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!visibleColumns[col.key]}
                        onChange={(e) => setVisibleColumns(prev => ({ ...prev, [col.key]: e.target.checked }))}
                        className="w-3.5 h-3.5 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {items.length > 0 && (
            <>
              <button onClick={() => setShowExportModal(true)}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm">
                <Download className="w-4 h-4" /> Exportar Excel
              </button>
              <button
                onClick={() => { if (window.confirm(`¿Vaciar los ${items.length} productos?`)) onClearItems(); }}
                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 text-sm">
                <Trash2 className="w-4 h-4" /> Vaciar
              </button>
            </>
          )}
          <button onClick={onAddItem}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Agregar Producto
          </button>
        </div>
      </div>

      {/* Selector de Lista de Precios */}
      {priceLists.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl flex-wrap">
          <Tag className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="text-sm font-medium text-blue-800 whitespace-nowrap">Lista de Precios:</span>
          <select
            value={pendingPriceListId}
            onChange={(e) => setPendingPriceListId(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-1.5 border border-blue-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="">Sin lista de precios</option>
            {priceLists.map(pl => (
              <option key={pl.id} value={String(pl.id)}>{pl.name}</option>
            ))}
          </select>
          <button
            onClick={handleApplyClick}
            disabled={applyingPriceList || !pendingPriceListId}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {applyingPriceList ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Aplicando...
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3" />
                Aplicar a toda la cotización
              </>
            )}
          </button>
          {missingPriceCount > 0 && (
            <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full whitespace-nowrap flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {missingPriceCount} producto{missingPriceCount !== 1 ? 's' : ''} sin precio en esta lista
            </span>
          )}
        </div>
      )}

      <BulkProductSelector
        products={availableBulkProducts}
        onAddProducts={onBulkAdd}
        formatCurrency={formatCurrency}
      />

      {items.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <Package className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No hay productos en esta cotización</p>
          <button onClick={onAddItem} className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
            Agregar producto manualmente
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-xl">
          {/* Cabecera */}
          <div className="flex bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {activeColumns.map(col => (
              <div key={col.key} className={`shrink-0 px-2 py-2.5 ${col.width}`}>{col.label}</div>
            ))}
            <div className="shrink-0 px-2 py-2.5 w-16 text-center"></div>
          </div>
          {/* Filas */}
          <div className="divide-y divide-gray-100">
            {items.map((item, index) => (
              <ProposalItemRow
                key={index}
                item={item}
                index={index}
                products={products}
                onUpdate={onUpdateItem}
                onDelete={onDeleteItem}
                onProductSelect={onProductSelect}
                formatCurrency={formatCurrency}
                priceLists={priceLists}
                selectedPriceListId={selectedPriceListId}
                missingPrice={!!(item._priceListMissing && item.product_id && selectedPriceListId)}
                onSelectAltPriceList={(altPriceListId) => onSelectAltPriceList && onSelectAltPriceList(index, altPriceListId)}
                visibleColumns={activeColumns}
              />
            ))}
          </div>
        </div>
      )}

      {/* Total General */}
      {items.length > 0 && (
        <div className={`rounded-xl p-6 border ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-green-50 border-green-200'}`}>
          <div className="flex justify-between items-center">
            <span className={`text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-800'}`}>Total:</span>
            <span className={`text-3xl font-bold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
              {formatCurrency(grandTotal)}
            </span>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportFieldsModal
          fields={EXPORT_FIELDS}
          onClose={() => setShowExportModal(false)}
          onExport={(selectedFields) => {
            setShowExportModal(false);
            handleExportExcel(selectedFields);
          }}
        />
      )}
    </div>
  );
};

// ─── Fila de Item individual ───
const ProposalItemRow = ({ item, index, products, onUpdate, onDelete, onProductSelect, formatCurrency, priceLists = [], selectedPriceListId = '', missingPrice = false, onSelectAltPriceList, visibleColumns = [] }) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [productSearch, setProductSearch] = useState(item.product_name || '');
  const [selectedAlt, setSelectedAlt] = useState('');
  const [dropdownPos, setDropdownPos] = useState(null);
  const productInputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setProductSearch(item.product_name || '');
  }, [item.product_name]);

  // El listado se renderiza en un portal para que no lo recorte el scroll de la tabla
  const updateDropdownPos = useCallback(() => {
    const rect = productInputRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 256);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 256 && rect.top > spaceBelow;
    setDropdownPos({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      width,
      maxHeight: Math.max(140, Math.min(240, (openUp ? rect.top : spaceBelow) - 16)),
    });
  }, []);

  useEffect(() => {
    if (!showAutocomplete) return;
    updateDropdownPos();
    const reposition = () => updateDropdownPos();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [showAutocomplete, updateDropdownPos]);

  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return [];
    const searchLower = productSearch.toLowerCase();
    return products.filter(p =>
      p.name?.toLowerCase().includes(searchLower) ||
      p.product_code?.toLowerCase().includes(searchLower) ||
      p.description?.toLowerCase().includes(searchLower)
    ).slice(0, 10);
  }, [products, productSearch]);

  const handleProductFromCatalog = (product) => {
    onProductSelect(product, index);
    setProductSearch(product.name || '');
    setShowAutocomplete(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const insideInput = productInputRef.current?.contains(event.target);
      const insideDropdown = dropdownRef.current?.contains(event.target);
      if (!insideInput && !insideDropdown) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cellCls = 'shrink-0 px-2 py-2 flex items-center';
  const inputCls = 'w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500/30 bg-white';
  const readCls = 'text-xs text-gray-700 truncate';

  const renderCell = (col) => {
    switch (col.key) {
      case 'product_image':
        return item.product_image
          ? <img src={item.product_image} alt="" className="w-10 h-10 object-cover rounded-lg border border-gray-200" />
          : <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center"><Image className="w-4 h-4 text-gray-300" /></div>;
      case 'product_name':
        return (
          <div className="relative w-full" ref={productInputRef}>
            <input type="text" value={productSearch}
              onChange={(e) => { setProductSearch(e.target.value); setShowAutocomplete(e.target.value.trim().length > 0); updateDropdownPos(); onUpdate(index, { product_name: e.target.value }); }}
              onFocus={() => { setShowAutocomplete(productSearch.trim().length > 0); updateDropdownPos(); }}
              placeholder="Buscar..."
              className={inputCls}
            />
            {showAutocomplete && filteredProducts.length > 0 && dropdownPos && createPortal(
              <div
                ref={dropdownRef}
                style={{
                  position: 'fixed',
                  left: dropdownPos.left,
                  top: dropdownPos.top,
                  bottom: dropdownPos.bottom,
                  width: dropdownPos.width,
                  maxHeight: dropdownPos.maxHeight,
                  zIndex: 9999,
                }}
                className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-y-auto"
              >
                {filteredProducts.map(p => (
                  <button key={p.id} type="button" onMouseDown={(e) => { e.preventDefault(); handleProductFromCatalog(p); }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs border-b border-gray-50 last:border-0 flex items-center gap-2">
                    {p.main_image ? <img src={p.main_image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" /> : <div className="w-8 h-8 bg-gray-100 rounded flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 truncate">{p.name}</div>
                      <div className="text-gray-500 truncate">{p.product_code} · {p.brand_name}</div>
                    </div>
                  </button>
                ))}
              </div>,
              document.body
            )}
          </div>
        );
      case 'quantity':
        return <input type="number" value={item.quantity ?? 1} min="0"
          onChange={(e) => onUpdate(index, { quantity: parseInt(e.target.value) || 0 })}
          className={`${inputCls} w-16 text-center font-semibold`} />;
      case 'price_fob_unit':
        return <input type="number" value={item.price_fob_unit ?? 0} min="0" step="0.01"
          onChange={(e) => onUpdate(index, { price_fob_unit: parseFloat(e.target.value) || 0 })}
          className={`${inputCls} w-20`} />;
      case 'moq':
        return <input type="number" value={item.moq ?? ''} min="0" placeholder="-"
          onChange={(e) => onUpdate(index, { moq: e.target.value ? parseInt(e.target.value) : null })}
          className={`${inputCls} w-16`} />;
      case 'total':
        return <span className="text-xs font-bold text-green-700 whitespace-nowrap">{formatCurrency(item.total || 0)}</span>;
      case 'product_code':      return <span className={readCls}>{item.product_code || '-'}</span>;
      case 'brand_name':        return <span className={readCls}>{item.brand_name || '-'}</span>;
      case 'category_name':     return <span className={readCls}>{item.category_name || '-'}</span>;
      case 'product_variant':   return <span className={readCls}>{item.product_variant || '-'}</span>;
      case 'description':       return <span className={`${readCls} text-gray-500`} title={item.description}>{(item.description||'').slice(0,60) || '-'}</span>;
      case 'link_web':          return item.link_web ? <a href={item.link_web} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline truncate block">{item.link_web}</a> : <span className={readCls}>-</span>;
      case 'packaging_type_name': return <span className={readCls}>{item.packaging_type_name || '-'}</span>;
      case 'product_size':      return <span className={readCls}>{item.product_size || '-'}</span>;
      case 'packaging_height':  return <span className={readCls}>{item.packaging_height ?? '-'}</span>;
      case 'packaging_length':  return <span className={readCls}>{item.packaging_length ?? '-'}</span>;
      case 'packaging_width':   return <span className={readCls}>{item.packaging_width ?? '-'}</span>;
      case 'master_pack_height':return <span className={readCls}>{item.master_pack_height ?? '-'}</span>;
      case 'master_pack_length':return <span className={readCls}>{item.master_pack_length ?? '-'}</span>;
      case 'master_pack_width': return <span className={readCls}>{item.master_pack_width ?? '-'}</span>;
      case 'master_pack_gw':    return <span className={readCls}>{item.master_pack_gw ?? '-'}</span>;
      case 'master_pack_nw':    return <span className={readCls}>{item.master_pack_nw ?? '-'}</span>;
      case 'pieces':            return <span className={readCls}>{item.pieces ?? '-'}</span>;
      case 'pcs_per_ctn':       return <span className={readCls}>{item.pcs_per_ctn ?? '-'}</span>;
      case 'inners':            return <span className={readCls}>{item.inners ?? '-'}</span>;
      case 'cbm_per_ctn':       return <span className={readCls}>{item.cbm_per_ctn ?? '-'}</span>;
      case 'moq_oem':           return <span className={readCls}>{item.moq_oem ?? '-'}</span>;
      case 'port_name':         return <span className={readCls}>{item.port_name || '-'}</span>;
      case 'battery_info':      return <span className={readCls} title={item.battery_info}>{(item.battery_info||'').slice(0,40) || '-'}</span>;
      case 'recommended_age':   return <span className={readCls}>{item.recommended_age || '-'}</span>;
      case 'material_composition': return <span className={readCls} title={item.material_composition}>{(item.material_composition||'').slice(0,30) || '-'}</span>;
      case 'manufacturer_code': return <span className={readCls}>{item.manufacturer_code || '-'}</span>;
      case 'assortment':        return <span className={readCls}>{item.assortment || '-'}</span>;
      case 'discount_fob':      return <span className={readCls}>{item.discount_fob != null ? `${item.discount_fob}%` : '-'}</span>;
      default:                  return <span className={readCls}>-</span>;
    }
  };

  return (
    <div className={`flex items-center hover:bg-blue-50/40 transition-colors ${missingPrice ? 'bg-orange-50' : ''}`}>
      {visibleColumns.map(col => (
        <div key={col.key} className={`${cellCls} ${col.width}`}>
          {renderCell(col)}
        </div>
      ))}
      {/* Acción eliminar */}
      <div className="shrink-0 w-16 px-2 py-2 flex items-center justify-center">
        <button onClick={() => onDelete(index)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// AbonosSection eliminada – ahora todo se gestiona mediante ProposalItemsTable

// Componente de Vista Previa Formal de la Cotización
const ProposalView = ({ proposal, contacts, onClose, onGeneratePDF }) => {
  const viewRef = useRef(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [companyData, setCompanyData] = useState({ name: '', data: '', logo: '' });

  // Cargar datos de empresa al montar
  useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const data = await settingsService.getCompanyData();
        setCompanyData(data);
      } catch (error) {
        console.error('Error cargando datos de empresa:', error);
      }
    };
    loadCompanyData();
  }, []);

  const contact = contacts.find(c => c.id === proposal.contactId?.toString());

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const handleGeneratePDFClick = async () => {
    setGeneratingPDF(true);
    try {
      await onGeneratePDF(proposal);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleGenerateShareLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await proposalService.generateShareToken(proposal.id);
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}/proposal/shared/${response.share_token}`;
      setShareLink(fullUrl);
    } catch (error) {
      console.error('Error generando link:', error);
      alert('No se pudo generar el link para compartir. Intentá de nuevo.');
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (error) {
        console.error('Error copiando link:', error);
        alert('No se pudo copiar el link. Copialo manualmente desde el campo de texto.');
      }
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="w-full flex flex-col">
        {/* Header con botones */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Vista Previa: {proposal.proposalId}</h2>
            <p className="text-sm text-gray-500 mt-1">Formato formal para el cliente</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerateShareLink}
              disabled={generatingLink}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {generatingLink ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generando...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4" />
                  {shareLink ? 'Link Generado' : 'Generar Link'}
                </>
              )}
            </button>
            {shareLink && (
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                <input
                  type="text"
                  value={shareLink}
                  readOnly
                  className="bg-transparent text-sm text-gray-700 outline-none flex-1 min-w-0 max-w-xs"
                />
                <button
                  onClick={handleCopyLink}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  {linkCopied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600" />
                  )}
                </button>
              </div>
            )}
            <button
              onClick={handleGeneratePDFClick}
              disabled={generatingPDF}
              className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {generatingPDF ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generando...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generar PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Contenido de la cotización */}
        <div className="flex-1 overflow-y-auto pr-2">
          <div
            id={`proposal-view-${proposal.id}`}
            ref={viewRef}
            className="bg-white mx-auto"
            style={{ 
              width: '210mm', // Ancho A4
              maxWidth: '210mm'
            }}
          >
            {/* Organizar contenido en páginas según saltos de página */}
            {(() => {
              // Función helper para renderizar una página
              const renderPage = (contentArray, isFirstPage = false, pageIndex = 0) => (
                <div 
                  key={isFirstPage ? 'first' : `page-${pageIndex}`}
                  className="proposal-page bg-white mb-4 shadow-lg relative"
                  style={{
                    width: '210mm',
                    minHeight: '297mm',
                    maxHeight: '297mm',
                    overflow: 'auto',
                    pageBreakAfter: 'always',
                    boxSizing: 'border-box',
                    paddingLeft: '15mm',
                    paddingRight: '15mm',
                    paddingTop: '15mm',
                    paddingBottom: '50mm' // Espacio para pie de página
                  }}
                >
                  {/* Logo de la empresa en margen superior izquierdo (en todas las páginas) */}
                  {companyData.logo && (
                    <div 
                      style={{
                        position: 'absolute',
                        top: '8mm',
                        left: '8mm',
                        zIndex: 10,
                        width: '40px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img
                        src={companyData.logo}
                        alt="Logo de la empresa"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          opacity: 1 // Mantener color original, no como marca de agua
                        }}
                      />
                    </div>
                  )}
                  
                  {contentArray}
                  {/* Pie de página */}
                  <div className="proposal-footer">
                    <div className="proposal-footer-company">
                      {companyData.name || 'VINSON TECH SRL – Sistemas de Gestión y POS'}
                    </div>
                    <div className="proposal-footer-contact">
                      {companyData.data || 'www.vinson.com.ar | soporte@vinson.com.ar'}
                    </div>
                  </div>
                </div>
              );

              // Encabezado y saludo (siempre en la primera página)
              const headerContent = (
                <>
                  {/* Encabezado Corporativo */}
                  <div className="proposal-header no-break">
                    <div className="text-right mt-3">
                      <div className="proposal-subtitle">
                        {(() => {
                          const date = new Date(proposal.createdAt);
                          const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
                          return `Buenos Aires, ${date.getDate()} de ${months[date.getMonth()]} de ${date.getFullYear()}`;
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Saludo formal */}
                  <div className="mb-4 proposal-section no-break">
                    <p className="text-sm text-gray-800 mb-2 proposal-paragraph no-break" style={{ lineHeight: '1.6' }}>
                      <strong>De nuestra mayor consideración:</strong>
                    </p>
                    {contact?.name && (
                      <p className="text-sm text-gray-800 mb-2 proposal-paragraph no-break" style={{ lineHeight: '1.6' }}>
                        {(proposal.salutation || 'Sr.')} {contact.name}
                      </p>
                    )}
                    {proposal.greeting ? (
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line proposal-paragraph no-break" style={{ lineHeight: '1.7', textAlign: 'justify' }}>
                        {proposal.greeting.replace(proposal.proposalId ? `(Ref. ${proposal.proposalId})` : '', '').trim()}
                        {proposal.proposalId && ` (Ref. ${proposal.proposalId})`}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-800 leading-relaxed proposal-paragraph no-break" style={{ lineHeight: '1.7', textAlign: 'justify' }}>
                        En virtud de lo conversado, Vinson tiene el agrado de presentarle una cotización comercial
                        {proposal.proposalId && ` (Ref. ${proposal.proposalId})`}
                        para la implementación de nuestras soluciones de Software POS.
                      </p>
                    )}
                  </div>
                </>
              );

              // Renderizar items estructurados
              const items = proposal.items || [];
              const pages = [];
              let currentPageContent = [headerContent];
              let isFirstPage = true;

              if (items.length > 0) {
                currentPageContent.push(
                  <div key="economic-title" className="mb-4 proposal-section no-break">
                    <h2 className="proposal-title mb-3 no-break" style={{ fontSize: '14px', marginTop: '20px' }}>PROPUESTA ECONÓMICA</h2>
                  </div>
                );

                const itemsContent = (
                  <div key="items-table" className="mb-3 proposal-section no-break">
                    <table className="proposal-table-modern no-break">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th style={{ textAlign: 'center', width: '60px' }}>Código</th>
                          <th style={{ textAlign: 'center', width: '70px' }}>Marca</th>
                          <th style={{ textAlign: 'center', width: '80px' }}>Cant. Pedida</th>
                          <th style={{ textAlign: 'right', width: '100px' }}>Precio FOB</th>
                          <th style={{ textAlign: 'right', width: '100px' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx}>
                            <td style={{ fontWeight: 400, fontSize: '10px' }}>{item.product_name || '-'}</td>
                            <td style={{ textAlign: 'center', fontSize: '10px' }}>{item.product_code || '-'}</td>
                            <td style={{ textAlign: 'center', fontSize: '10px' }}>{item.brand_name || '-'}</td>
                            <td style={{ textAlign: 'center', fontSize: '10px' }}>{item.quantity || 0}</td>
                            <td style={{ textAlign: 'right', fontSize: '10px' }}>
                              {formatCurrency(item.price_fob_unit || 0)}
                            </td>
                            <td style={{ textAlign: 'right', fontWeight: 500, fontSize: '10px' }}>
                              {formatCurrency(item.total || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );

                currentPageContent.push(itemsContent);

                // Total general
                const grandTotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
                currentPageContent.push(
                  <div key="totals" className="proposal-total-box no-break proposal-section" style={{ marginTop: '24px', width: 'fit-content', marginLeft: 'auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                      <span className="proposal-total-label" style={{ fontSize: '12px' }}>TOTAL:</span>
                      <span style={{ fontSize: '14px', fontWeight: 700 }}>
                        {formatCurrency(grandTotal)}
                      </span>
                    </div>
                  </div>
                );
              }

              // Agregar la última página si hay contenido
              if (currentPageContent.length > 0) {
                pages.push(renderPage(currentPageContent, isFirstPage, pages.length));
              }

              return pages.length > 0 ? pages : [renderPage([headerContent], true, 0)];
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalManager;
